import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import useSidebarResizing from '../hooks/useSidebarResizing';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Modal, ListGroup, Badge, Spinner, Dropdown } from 'react-bootstrap';
import { resolveCover, DEFAULT_PLAYLIST_COVER, playlistCoverUrl } from '../utils/cover';
import { positionMenuAtPoint, positionMenuByTrigger } from '../utils/menuPosition';
import { resolveTrackSrc } from '../utils/resolvePlaybackSrc';
// `isSamplesOnly` covers web + iOS + Android — anywhere full-stream extraction
// (yt-dlp via Electron IPC) isn't available. Use it for playability gating.
import { isWeb, isSamplesOnly, isNative } from '../utils/webOnly';
import {
    setPlaylists,
    setSelectedPlaylist,
    setSelectedPlaylistName,
    setLoading,
    clearSelection,
    toggleSidebar,
    setSidebarOpen,
    setSidebarWidth,
    setShowSavePlayerModal,
    triggerPlaylistRefresh
} from '../redux/slices/playlistSlice';
import { toggleLikesSidebar, setLikesSidebarOpen } from '../redux/slices/likesSlice';
import { toggleLikedMixesSidebar, setLikedMixesSidebarOpen } from '../redux/slices/likedMixesSlice';
import { toggleFollowedArtistsSidebar, setFollowedArtistsSidebarOpen } from '../redux/slices/followedArtistsSlice';
import { toggleRekordboxSidebar, setRekordboxSidebarOpen } from '../redux/slices/rekordboxSlice';
import { toggleSeratoSidebar, setSeratoSidebarOpen } from '../redux/slices/seratoSlice';
import { toggleTraktorSidebar, setTraktorSidebarOpen } from '../redux/slices/traktorSlice';
import { toggleVirtualDJSidebar, setVirtualDJSidebarOpen } from '../redux/slices/virtualdjSlice';
import { setAudioList, setAutoPlay, setQueueOwner } from '../redux/slices/musicPlayerSlice';
import { clearListContext } from '../redux/slices/listContextSlice';
import { setActiveTab, setFiles, setAutoAnalyze } from '../redux/slices/appSlice';
import { getLastExpandedPlaylistId, setLastExpandedPlaylistId, OPEN_PLAYLIST_EVENT } from '../utils/playlistOpenRequest';
import SidebarSearchField from './common/SidebarSearchField';
import ViewToggle, { useViewMode } from './common/ViewToggle';
import { pushNavEntry } from '../redux/slices/appSlice';
import { navigateHistory } from '../redux/thunk/appNavThunk';
import { registerUserScopedReset } from '../utils/userScopedCaches';
import { setActiveSearch, setTrackQuery, setMixQuery } from '../redux/slices/djpalSlice';
import { searchTrack, searchMixes } from '../redux/thunk/djpalThunk';
import { delayedClick, useMixPlayback } from './DJPal/MixPlayer';
import TrackContextMenu from './TrackContextMenu';
import { cleanDisplayTitle } from '../hooks/useEnrichedTracks';
import { shapeServerTracks, loadCloudPlaylistFiles, localPathFromTrack, upgradeLegacyRow, upgradeLegacyRows, restorableBackupItems } from '../utils/cloudPlaylistTracks';
import { restoreFilesWithPrompt } from '../utils/cloudBackup';
import { refreshAndRetry } from '../redux/utils/refreshAndRetry';
import { useScrollMemory } from '../hooks/useScrollMemory';
import { useIsMobileWeb } from '../hooks/useIsMobileWeb';
import MobileListSearch, { matchesQuery } from './common/MobileListSearch';
import { findJinkeQueueItem } from '../utils/jinkeQueueDom';
import { setLoudnessFiles, setLoudnessAutoAnalyze } from '../redux/slices/loudnessSlice';
import { setFiles as setMetadataFiles, setMetadataAutoAnalyze } from '../redux/slices/fixMetadataSlice';
import { setOrganizerFiles, setOrganizerAutoAnalyze } from '../redux/slices/organizerSlice';
import { setInfoFiles, setInfoAutoAnalyze, setInfoPlaylistName, addEnrichedFilesBatch, resetEnrichedFiles, recomputeGraphData } from '../redux/slices/infoSlice';
import { setSelectedPlaylist as setSmartRecsPlaylist, clearResults as clearSmartRecsResults } from '../redux/slices/smartRecsSlice';
import { fetchPlaylistRecommendations } from '../redux/thunk/smartRecsThunk';
import { useToast } from '../contexts/ToastContext';
import rekordboxLogo from '../assets/rekordbox-logo.png';
import { SourceGlyph, SOURCE_LABELS } from './SourceGlyph';
import seratoLogo from '../assets/serato-logo-10px.png';
import traktorLogo from '../assets/traktor-logo.png';
import virtualdjLogo from '../assets/virtual-dj-logo.png';
import mixgoatGlyph from '../assets/source-glyph-mixgoat.png';
import { ReactComponent as AnalyzeIcon } from '../assets/icons/analyze-files.svg';
import { ReactComponent as LoudnessIcon } from '../assets/icons/match-loudness.svg';
import { ReactComponent as MetadataIcon } from '../assets/icons/edit-filenames.svg';
import { ReactComponent as OrganizeIcon } from '../assets/icons/select-folder.svg';
import { ReactComponent as VisualizeIcon } from '../assets/icons/audio-file.svg';
import { track as trackEvent } from '../utils/analytics';
import { importSourceOf, connectionsOf, matchesConnection, connectionCounts, CONNECTION_APPS } from '../utils/playlistConnections';
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import SwipeToRevealRow from './SwipeToRevealRow';
import SideNav from './v2/SideNav';
import { useDesignV2 } from '../utils/designV2';
import './PlaylistSidebar.scss';
import './DjChangeBanner.scss';
import './TrackContextMenu.scss';
import { errMsg } from '../redux/utils/errMsg';
import CoverArt from './CoverArt';
import { persistPlaylistSidebarWidth, PLAYLIST_SIDEBAR_MIN_WIDTH, PLAYLIST_SIDEBAR_MAX_WIDTH } from '../redux/utils/djSidebarWidth';

// Mobile-only ⋯ menu for a playlist row. On desktop the row reveals
// edit/delete buttons on hover; on touch devices that hover state
// fires while scrolling, so we surface those actions through this
// kebab instead. The "Open in" inline dropdown is also hidden on
// mobile (handleOpenInTab is desktop-only), so this menu carries the
// only mobile-relevant action: Player Queue.
const PlaylistRowMenu = ({ playlist, onEdit, onDelete, onPlayerQueue }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useLayoutEffect(() => {
        if (!open || !btnRef.current) {
            if (!open) setStyle(null);
            return;
        }
        const rect = btnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 140;
        const openUp = spaceBelow < dropdownHeight;
        setStyle({
            position: 'fixed',
            right: window.innerWidth - rect.right,
            ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
            zIndex: 2000,
        });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (
                menuRef.current && !menuRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="track-more-wrapper playlist-row-menu" onClick={(e) => e.stopPropagation()}>
            <button
                ref={btnRef}
                className="track-more-btn playlist-more-btn"
                title="More options"
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="2.5" />
                    <circle cx="12" cy="12" r="2.5" />
                    <circle cx="19" cy="12" r="2.5" />
                </svg>
            </button>
            {open && style && ReactDOM.createPortal(
                <div className="track-context-dropdown" ref={menuRef} style={style} onClick={(e) => e.stopPropagation()}>
                    <div
                        className="dropdown-item"
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onPlayerQueue(playlist.id); }}
                    >
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" />
                            </svg>
                        </span>
                        Player Queue
                    </div>
                    <div
                        className="dropdown-item"
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onEdit(playlist); }}
                    >
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                        </span>
                        Edit
                    </div>
                    <div
                        className="dropdown-item"
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(playlist); }}
                    >
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                        </span>
                        Delete
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const isDesktop = typeof window !== 'undefined' && window.electron?.db;

// Stable drag id for a playlist row: server row id (cloud), else the local
// file path, else the index.
// The sidebar's search field. Both searches in this sidebar render it — the
// playlists list and the rows of an open playlist — so the slot keeps the same
// control (and the same neutral focus) either side of opening a playlist.
const sortableIdOf = (track, idx) => (track?.id != null ? `row:${track.id}` : track?.filePath ? `path:${track.filePath}` : `idx:${idx}`);

// Wraps one track row in dnd-kit's sortable hook and hands the ref/style/
// listeners to the row via a render prop, so the row's own click/play
// handlers are untouched. Drag starts only after an 8px move (PointerSensor
// activation), so taps and clicks still open/play.
function SortableTrackRow({ id, disabled, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    const style = {
        transform: DndCSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { opacity: 0.6, position: 'relative', zIndex: 2 } : null),
    };
    return children({ setNodeRef, style, dragProps: disabled ? {} : { ...attributes, ...listeners }, isDragging });
}
// Phone: playlist rows swipe left to reveal Delete (same pattern as
// Favourite Artists). The ⋯ kebab stays — it also carries Queue / Rename.
const isTouchPhone = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 768px)').matches
    && window.matchMedia('(hover: none)').matches;

// Per-user playlists cache. The server is the source of truth; this just
// gives instant first paint on mount + on a same-user re-login. Keyed by
// the MixGoat user id so signing in as a different account always misses
// (no cross-account leakage from a stale cache).
const PLAYLISTS_CACHE_KEY_PREFIX = 'mg:playlists:cache:';
const PLAYLISTS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function readPlaylistsCache(userId) {
    if (!userId) return null;
    try {
        const raw = localStorage.getItem(PLAYLISTS_CACHE_KEY_PREFIX + userId);
        if (!raw) return null;
        const snap = JSON.parse(raw);
        if (!snap || typeof snap !== 'object') return null;
        if (Date.now() - (snap.savedAt || 0) > PLAYLISTS_CACHE_TTL_MS) return null;
        return Array.isArray(snap.playlists) ? snap.playlists : null;
    } catch { return null; }
}

// Inline base64 covers have no business in this cache: with a few hundred
// playlists they once pushed one cache entry to ~49MB, and the synchronous
// localStorage read + JSON.parse of that string on every sidebar open was a
// 1-2s UI-thread stall. The server now sends cover URLs, but rows synced by
// an older server (or any other data: leftovers) still get stripped here —
// the cache paints a placeholder and the live fetch supplies the real cover.
const CACHED_COVER_MAX_CHARS = 8 * 1024;
function cacheSafePlaylist(p) {
    if (typeof p?.cover_image === 'string'
        && p.cover_image.startsWith('data:')
        && p.cover_image.length > CACHED_COVER_MAX_CHARS) {
        return { ...p, cover_image: null };
    }
    return p;
}

function writePlaylistsCache(userId, playlists) {
    if (!userId) return;
    try {
        localStorage.setItem(
            PLAYLISTS_CACHE_KEY_PREFIX + userId,
            JSON.stringify({ savedAt: Date.now(), playlists: (playlists || []).map(cacheSafePlaylist) }),
        );
    } catch {
        // Quota / private-browsing failures are non-fatal — Redux still
        // drives the live session, we just lose the instant-paint shortcut.
    }
}

// Tracks loaded into the playlist view come in two shapes:
//   - Server (web/cloud library): { title, artist, artwork, youtube_id, ... } plus
//     { name, musicSrc, cover } added by handleLoadPlaylist for the player.
//   - Local SQLite (toPlayerFormat): { name, singer, cover, musicSrc } only.
// TrackContextMenu and Mixpedia search expect title/artist/artwork — normalize.
const adaptTrackForMixpedia = (track) => ({
    ...track,
    title: track.title || track.name,
    artist: track.artist || track.singer,
    artwork: track.artwork || track.artworkUrl || track.image || track.cover,
    youtubeId: track.youtubeId || track.youtube_id,
    soundcloudUrl: track.soundcloudUrl || track.soundcloud_url,
    previewUrl: track.previewUrl || track.preview_url || track.preview,
    // Catalog id: cloud rows added the modern way persist track_public_id /
    // trackPublicId but not djpalId — without this mapping, "Open in
    // Mixpedia" silently fell to a text search for those rows.
    djpalId: track.djpalId || track.track_public_id || track.trackPublicId || null,
    // Real on-disk path (cloud rows resolved by shapeServerTracks, local rows
    // from toPlayerFormat) — lets TrackContextMenu offer "Show in Finder".
    filePath: track.filePath || track._localPath || null,
    // Mix identity (shapeServerTracks maps mix_public_id) — routes clicks to
    // the MIX page instead of a track search that has nothing to find.
    mixPublicId: track.mixPublicId || track.mix_public_id || null,
});

// Only remote URLs are safe to persist server-side — localhost audio-server
// URLs (/audio, /lazy-audio, /stream-proxy) are machine-specific.
const nonLocalhostUrl = (u) => (u && !u.startsWith('http://localhost:3069/') ? u : '');

// Which playlist was open when this view last went away. Switching tabs
// unmounts the whole Library page (App.js renders it on `activeTab`), so an
// open playlist would collapse back to the list every time the user stepped
// out to a mix and came back — the one moment they expect to land where they
// left. Module level for the same reason useScrollMemory is: component state
// dies exactly when the user returns.
// (Lives in utils/playlistOpenRequest.js so toast links can pre-set it.)

// Last-fetched tracks per playlist, so re-entering a playlist (or returning
// to the Library tab with one open) paints the rows TOGETHER with the hero
// instead of hero-first-then-rows — the same quiet-refresh recipe the feed
// uses: paint the cache instantly, refetch silently, replace when it lands.
// Module level for the same unmount reason as _lastExpandedPlaylistId.
const _expandedTracksCache = new Map();

// Both mount-restore memories hold the ACCOUNT's playlists — wipe them with
// the rest of the user-scoped caches so the next account on this device
// can't restore into the previous user's expanded playlist.
registerUserScopedReset(() => {
    _expandedTracksCache.clear();
    setLastExpandedPlaylistId(null);
});

// A playlist cover URL is only usable where the machine that produced it is.
// Local-file playlists get their cover backfilled from the first track's
// embedded art, which desktop serves off its own audio server — a URL that
// resolves nowhere else. Off-desktop, treat those as "no cover" so the
// placeholder renders instead of a dead <img>.
const usableCoverUrl = (u) => (isWeb ? nonLocalhostUrl(u) : (u || ''));

// DJ-app imports show their REAL cover (first track with art, like any other
// playlist) plus a small source badge in the corner. Connection resolution
// (live playlist_sources links with the description as fallback) lives in
// utils/playlistConnections.js — a playlist synced with several tools is a
// normal state and must badge/filter under all of them.


// The music-note placeholder every cover slot falls back to.
// Branded gradient tile (utils/cover.js) rather than a faint glyph on the
// panel colour, so a cover-less playlist never reads as a black square.
const hueOf = (seed) => { let h = 0; for (const ch of String(seed || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return h % 360; };
const PlaylistCoverFallback = ({ className = 'playlist-cover-placeholder', seed }) => (
    <div className={className} style={seed ? { '--pl-hue': hueOf(seed) } : undefined}>
        <img src={DEFAULT_PLAYLIST_COVER} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </div>
);

// Cover <img> that degrades to the placeholder instead of leaving the
// browser's broken-image glyph on screen. A cover URL can die for reasons the
// render path can't see up front (host unreachable, art deleted, a stale
// machine-local URL that slipped through), so failure is handled where it
// surfaces rather than predicted.
// `source` (rekordbox | serato | traktor | virtualdj) overlays a small app
// logo in the corner so imported playlists stay recognisable as imports.
const PlaylistCoverImg = ({ src, alt, wrapClassName, fallbackClassName, fallbackSize, sources, seed }) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => { setFailed(false); }, [src]);
    // One glyph (the first connection) keeps the badge quiet; the tooltip
    // carries the full set ("Syncs with Serato & Traktor").
    const list = (Array.isArray(sources) ? sources : []).filter(s => SOURCE_LABELS[s]);
    const source = list[0] || null;
    const badge = source ? { label: list.map(s => SOURCE_LABELS[s]).join(' & ') } : null;
    const inner = (!src || failed)
        ? <PlaylistCoverFallback size={fallbackSize} className={fallbackClassName} seed={seed} />
        // lazy + async: covers are now server URLs (some still large) — with
        // hundreds of rows, eager synchronous decodes were a scroll stutter.
        : <div className={wrapClassName}><img src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)} /></div>;
    if (!badge) return inner;
    return (
        // Tooltip lives on the wrapper: the badge itself is pointer-events:
        // none (so it never blocks cover clicks), meaning its own title can
        // never fire — and putting it on the whole row read as noise.
        <div className="cover-with-source" title={`Syncs with ${badge.label}`}>
            {inner}
            <span className={`cover-source-badge source-${source}`} role="img" aria-label={badge.label}>
                <SourceGlyph source={source} size={wrapClassName === 'hero-cover' ? 20 : 16} />
            </span>
        </div>
    );
};

const PlaylistSidebar = ({ asPage = false }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    // Mix rows play through the real mix pipeline (extract + mix player),
    // exactly like feed / mix-page plays. loadingMixId/playingMixId drive
    // the row's spinner / pause icon (keyed by the mix public id).
    const { playMix, loadingMixId, playingMixId } = useMixPlayback();
    // After clicking a rail toggle, suppress the hover-expand until the
    // cursor actually leaves the rail. Otherwise the rail would re-expand
    // under the still-hovering cursor right after the user closed a panel.
    const [railJustClicked, setRailJustClicked] = useState(false);
    const designV2 = useDesignV2();
    const [viewMode, setViewMode] = useViewMode('playlists');
    const navStack = useSelector(state => state.app.navStack);
    const navIndex = useSelector(state => state.app.navIndex);
    const activeCategory = useSelector(state => state.app.activeCategory);
    const handleRailClick = () => setRailJustClicked(true);
    const handleRailMouseLeave = () => setRailJustClicked(false);
    const {
        playlists,
        selectedPlaylistId,
        loading,
        sidebarOpen,
        sidebarWidth,
        selectedTracks,
        showSavePlayerModal,
        refreshTrigger
    } = useSelector(state => state.playlist);
    const accessToken = useSelector(state => state.auth.accessToken);
    const currentUserId = useSelector(state => state.auth.user?.id || null);

    const { audioList, autoPlay, queueOwner } = useSelector(state => state.musicPlayer);
    const { sidebarOpen: likesSidebarOpen } = useSelector(state => state.likes);
    const { sidebarOpen: likedMixesSidebarOpen } = useSelector(state => state.likedMixes);
    const { sidebarOpen: followedArtistsSidebarOpen } = useSelector(state => state.followedArtists);
    const { sidebarOpen: rekordboxSidebarOpen } = useSelector(state => state.rekordbox);
    const { sidebarOpen: seratoSidebarOpen } = useSelector(state => state.serato);
    const { sidebarOpen: traktorSidebarOpen } = useSelector(state => state.traktor);
    const { sidebarOpen: virtualdjSidebarOpen } = useSelector(state => state.virtualdj);
    const djChangesPending = useSelector(state => state.djLibraryChanges?.pending || {});
    const activeTab = useSelector(state => state.app.activeTab);

    // Current files in each tab (for append mode)
    const appFiles = useSelector(state => state.app.files);
    const loudnessFiles = useSelector(state => state.loudness.loudnessFiles);
    const metadataFiles = useSelector(state => state.fixMetadata.files);
    const organizerFiles = useSelector(state => state.organizer.organizerFiles);
    const infoFiles = useSelector(state => state.info.infoFiles);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [editingPlaylist, setEditingPlaylist] = useState(null);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
    const [coverImage, setCoverImage] = useState(null);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // Source chips: 'all' | 'mixgoat' | rekordbox/serato/traktor/virtualdj.
    // Session-local on purpose — a sticky filter that survives restarts reads
    // as "my playlists disappeared".
    const [sourceFilter, setSourceFilter] = useState('all');
    // Live DJ-app links from the local DB ({ byLocalId, byServerId }) — the
    // cloud rows only carry single-valued provenance. Absent on web /
    // signed-out; connectionsOf falls back to the description then.
    const [sourceLinksMaps, setSourceLinksMaps] = useState(null);
    const isMobileWeb = useIsMobileWeb();
    const [isResizing, setIsResizing] = useState(false);
    useSidebarResizing(isResizing);
    const expandedRef = useRef(null);
    const widthRef = useRef(sidebarWidth);
    widthRef.current = sidebarWidth;
    const [contextMenu, setContextMenu] = useState(null);
    const [contextMenuPos, setContextMenuPos] = useState(null); // clamped {left, top}; null until measured
    const [contextMenuPlaylist, setContextMenuPlaylist] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    // The "Open in ▾" dropdown flips above its button when it would open below
    // the fold (rows near the bottom of the scrolled sidebar list).
    const [openDropdownUp, setOpenDropdownUp] = useState(false);
    const actionDropdownRef = useRef(null);
    const [expandedPlaylistId, setExpandedPlaylistId] = useState(getLastExpandedPlaylistId());
    // Leave-and-return keeps the playlist LIST's scroll position (the
    // expanded-playlist view resets on unmount, so only the root list is
    // tracked — `active` pauses saving while a playlist is expanded).
    const listScrollRef = useScrollMemory('playlists', [playlists.length], { active: !expandedPlaylistId });
    const [expandedPlaylistTracks, setExpandedPlaylistTracks] = useState(
        () => (getLastExpandedPlaylistId() && _expandedTracksCache.get(getLastExpandedPlaylistId())) || []
    );
    // Narrows the OPEN playlist's rows (title + artist). Per-playlist and
    // never persisted: a filter the user can't see the origin of is how rows
    // go quietly missing, so it clears on every open and on Back.
    // NOT named trackQuery/setTrackQuery: this file imports djpalSlice's
    // `setTrackQuery` (line ~36) and a same-named setter shadows it, turning
    // the row's "open in Mixpedia" dispatch into dispatch(undefined).
    const [playlistFilter, setPlaylistFilter] = useState('');
    const playlistFilterFiredRef = useRef(false); // analytics: once per open
    // Restoring an open playlist with no cached rows yet must show the
    // spinner, not a flash of "No tracks in this playlist" while the mount
    // fetch runs.
    const [expandedPlaylistLoading, setExpandedPlaylistLoading] = useState(
        () => !!(getLastExpandedPlaylistId() && !_expandedTracksCache.has(getLastExpandedPlaylistId()))
    );
    const [currentlyPlayingPath, setCurrentlyPlayingPath] = useState(() => window.__currentPlayerMusicSrc || null);
    const [isPlaying, setIsPlaying] = useState(false);
    const contextMenuRef = useRef(null);
    const dropdownRef = useRef(null);

    // Stay in sync with the player — auto-advance, skip, external play all update this event
    useEffect(() => {
        const onTrackChange = (e) => {
            setCurrentlyPlayingPath(e.detail.musicSrc || null);
            setIsPlaying(e.detail.playing !== false);
        };
        window.addEventListener('player-track-change', onTrackChange);
        return () => window.removeEventListener('player-track-change', onTrackChange);
    }, []);

    // Sync pause/resume from Redux (covers play-all, external pause buttons)
    useEffect(() => {
        setIsPlaying(autoPlay);
    }, [autoPlay]);

    const handlePauseTrack = (e) => {
        e.stopPropagation();
        // Native: jinke's element is muted and drives nothing — route through
        // the hijacked play/pause button (the same door NowPlayingPage's
        // transport uses), which pauses the plugin with all its bookkeeping.
        if (isNative) {
            const btn = document.querySelector('.react-jinke-music-player-main .player-content .play-btn');
            if (btn) { btn.click(); return; }
        }
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
        }
        setIsPlaying(false);
        dispatch(setAutoPlay(false));
    };

    const handleOpenInMixpedia = useCallback((track) => {
        const adapted = adaptTrackForMixpedia(track);
        // Mix rows open the MIX page id-first (same pattern as feed rows) —
        // running a TRACK search on a mix title lands on "not found".
        if (adapted.mixPublicId) {
            dispatch(setMixQuery(adapted.title || ''));
            dispatch(setActiveSearch('mix'));
            dispatch(setActiveTab('djpal'));
            dispatch(searchMixes({ filters: { public_id: adapted.mixPublicId } }));
            trackEvent('mix_opened', { mix_id: adapted.mixPublicId, artist: adapted.artist || undefined, from: 'playlist_sidebar' });
            return;
        }
        const query = adapted.artist ? `${adapted.artist} - ${adapted.title}` : (adapted.title || '');
        if (!query) return;
        // Sub-tab + query BEFORE setActiveTab so the nav marker is typed and the
        // result replaces it (one Back = back here). setActiveTab sets the
        // category itself — a separate setActiveCategory pushes a stray
        // landing-page entry into the back stack.
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(query));
        dispatch(setActiveTab('djpal'));
        setTimeout(() => {
            // djpalId only — playlist rows' `id` is a local/server row id, not
            // the catalog id. Absent djpalId falls back to the name search.
            dispatch(searchTrack({ trackName: query, trackId: adapted.djpalId || track.djpalId || null, variant: 'A', artworkUrl: adapted.artworkUrl || adapted.artwork || track.artwork || null }));
        }, 150);
    }, [dispatch]);

    // Remove a row from the expanded CLOUD playlist by its row PK (`track.id`
    // on server rows). Local-file and mix rows have no catalog track_id, so
    // this row-level endpoint is the only removal path that works for every
    // row shape.
    const handleRemoveFromPlaylist = useCallback(async (track) => {
        const rowId = track?.id;
        if (!rowId || !expandedPlaylistId) return;
        try {
            const res = await refreshAndRetry((token) => window.electron.library.removePlaylistRow(token, expandedPlaylistId, rowId));
            if (res?.error) { toast.error(typeof res.error === 'string' ? res.error : 'Failed to remove from playlist'); return; }
            toast.success('Removed from playlist');
            trackEvent('playlist_track_removed', { from: 'playlist_sidebar', is_mix: !!(track.mixPublicId || track.mix_public_id) });
            dispatch(triggerPlaylistRefresh());
            loadPlaylists(); // refresh sidebar track counts
        } catch (e) {
            toast.error('Failed to remove from playlist');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedPlaylistId, dispatch]);

    // TrackContextMenu's onSearch signature: (event, track) => void
    const handleContextMenuSearch = useCallback((e, track) => {
        if (e?.stopPropagation) e.stopPropagation();
        handleOpenInMixpedia(track);
    }, [handleOpenInMixpedia]);

    // Replacing audioList via reset+addTrack flickers the playlist view because
    // the player UI unmounts while the queue is empty. Prefer in-place switches
    // when the track is already loaded or queued.
    const handlePlayTrackFromPlaylist = useCallback(async (track, idx) => {
        // MIX rows play as mixes: the row stores identity (mix_public_id),
        // not a play link — a Mixcloud mix has no YT/SC link to persist at
        // add time. Fetch the mix by id and hand it to the mix player (the
        // same pipeline as feed / mix-page plays). Desktop only —
        // extractStreamUrl is an IPC without a web shim.
        const mixPid = track.mixPublicId || track.mix_public_id || null;
        if (mixPid && isDesktop && window.electron?.djpal?.searchMixes) {
            try {
                const token = (() => { try { return localStorage.getItem('djpal:token'); } catch { return null; } })();
                // Lite variant — playMix only needs the mix's link/title/cover,
                // not the tracklist join.
                const search = window.electron.djpal.searchMixesLite || window.electron.djpal.searchMixes;
                const res = await search(token, { public_id: mixPid }, null, { page: 1, limit: 1 });
                const data = res?.data || res;
                const arr = data?.results || data?.result || [];
                const mix = Array.isArray(arr) ? arr[0] : null;
                if (mix?.link) {
                    await playMix(mix);
                    return;
                }
            } catch (_) { /* fall through to the toast below */ }
            toast.warning('Couldn’t load this mix — open it in Mixpedia to play it.', 6000);
            return;
        }
        // Legacy cloud rows (no catalog id, no YT/SC link — pre-2026 adds
        // only stored artist/title/sample, or nothing but the name) shape to
        // a plain sample musicSrc or none at all (missingLocal). Try a
        // ONE-TIME catalog match by name on click: a hit upgrades this play
        // (and the row queued below) to the full-playback /lazy-audio path,
        // with the sample kept as the endpoint's fallback. Runs BEFORE the
        // missingLocal bail — a pure-online row with no source can still
        // become playable here. Rows resolved to a synced-away local file
        // (_localPath set) keep the honest "on your other device" toast.
        const originalTrack = track;
        const catalogCtx = useServer ? { playlistId: expandedPlaylistId, accessToken } : {};
        track = await upgradeLegacyRow(track, catalogCtx);
        // File lives on another machine (synced metadata only) or has no
        // source at all (and the catalog match above came up dry) — don't
        // try to play it: it would 404 / no-op. Tell the user why instead
        // of failing silently.
        if (track.missingLocal) {
            const label = track.name || track.title || 'This track';
            if (track._localPath) {
                toast.warning(`"${label}" isn't on this device — it's on the device you synced it from.`, 6000);
            } else {
                toast.warning(`Couldn't find a playable source for "${label}".`, 6000);
            }
            return;
        }
        // Src-less but resolvable row (no sample / no local file, but a YT/SC
        // link or music-info id — the no-artwork class): resolve now, exactly
        // like the play path everywhere else, then queue with the result.
        // Pass an explicit object — spreading the row would leak its local
        // playlist-row integer `id` into entityId().
        if (!track.musicSrc && isDesktop) {
            const resolved = await resolveTrackSrc({
                title: track.name || track.title || '',
                artist: track.singer || track.artist || '',
                youtubeId: track.youtubeId || null,
                soundcloudUrl: track.soundcloudUrl || null,
                id: track.track_public_id || null,
                duration: track.duration,
            });
            if (!resolved) {
                toast.warning('Could not load track audio');
                return;
            }
            track = { ...track, musicSrc: resolved.musicSrc, ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}) };
        }
        const trackSrc = track.musicSrc;
        if (!trackSrc) return;

        // Native: the resume and queue-jump branches below act on jinke's
        // muted element / hidden DOM, which the plugin never hears. When the
        // track is already in the queue, ask the player to jump the PLUGIN to
        // it by URL (resolved against the native queue mirror in
        // MusicPlayer); a same-URL ask resumes instead of restarting.
        if (isNative && (currentlyPlayingPath === trackSrc || audioList.some(t => t.musicSrc === trackSrc))) {
            window.dispatchEvent(new CustomEvent('mixgoat-native-play-url', { detail: { url: trackSrc } }));
            dispatch(setAutoPlay(true));
            return;
        }

        // Track currently loaded — just resume (covers "played through, click
        // the same track again" — no audioList churn).
        if (currentlyPlayingPath === trackSrc) {
            const audio = window.musicPlayerInstance;
            audio?.play?.()?.catch(() => {});
            dispatch(setAutoPlay(true));
            return;
        }

        // Track is in the current queue — switch via jinke's hidden audio-item
        // DOM click. Same trick CustomPlaylistPanel uses; no flicker.
        const queueIdx = audioList.findIndex(t => t.musicSrc === trackSrc);
        if (queueIdx >= 0) {
            const items = document.querySelectorAll('.react-jinke-music-player-main .audio-lists-panel .audio-item');
            if (items[queueIdx]) {
                items[queueIdx].click();
                return;
            }
        }

        // Track not in queue — replace the queue with this playlist starting
        // from the clicked track. setAudioList replaces in place; going through
        // resetAudioList would briefly empty the queue, which fires jinke's
        // onAudioListsChange(0) → clears currentTrackInfo → makes the player's
        // like button disappear until the next track starts.
        // Samples-only platforms (web, iOS, Android): only tracks with a
        // sampleUrl/preview-derived musicSrc are playable. Desktop allows
        // tracks with full-play URLs (YT/SC) to be played via yt-dlp.
        // Drop tracks that aren't on this device so the queue doesn't cascade
        // through unplayable 404s after the clicked track. Samples-only
        // platforms additionally require a musicSrc.
        // Swap the clicked row for its resolved copy, and require a musicSrc —
        // src-less rows would be silently dropped by setAudioList's validation
        // anyway, which would desync the queue indices.
        const isQueueable = t => !t.missingLocal && t.musicSrc;
        // Upgrade the REST of the playlist too, not just the clicked row: the
        // queue auto-advances into these, and an un-upgraded legacy row plays
        // its 30-second sample. Memoized + bounded — a no-op for playlists
        // whose rows already carry a catalog id or link.
        const upgradedRows = await upgradeLegacyRows(
            expandedPlaylistTracks.map(t => (t === originalTrack ? track : t)),
            catalogCtx
        );
        cacheExpandedTracks(expandedPlaylistId, upgradedRows);
        const playable = upgradedRows.filter(isQueueable);
        const playIdx = playable.findIndex(t => t.musicSrc === trackSrc);
        const reordered = playIdx >= 0
            ? [...playable.slice(playIdx), ...playable.slice(0, playIdx)]
            : playable;
        if (!reordered.length) return;
        // The queue IS this playlist now — a listContext left over from an
        // earlier feed/Mixpedia play must not survive it. On native the queue
        // effect prefers a 'tracks' context over audioList, so a stale one
        // made Play All splice the playlist into the OLD surface and keep the
        // old track playing; on desktop it made the playlist resume the old
        // list when it ran out.
        dispatch(clearListContext());
        dispatch(setAudioList(reordered));
        dispatch(setAutoPlay(true));
    }, [dispatch, expandedPlaylistTracks, audioList, currentlyPlayingPath, playMix]);

    // Load playlists on mount, when auth changes, or when explicitly refreshed.
    // Re-running on accessToken change also covers the user-switch case — the
    // previous user's Redux array is cleared in loadPlaylists before the new
    // user's cache / server response lands, so nothing leaks across.
    useEffect(() => {
        loadPlaylists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger, accessToken, currentUserId]);

    // Refresh the DJ-app links whenever the list refreshes (imports and
    // unsyncs land through the same refresh triggers).
    useEffect(() => {
        window.electron?.db?.playlists?.getSourceLinks?.()
            .then((m) => setSourceLinksMaps(m || null))
            .catch(() => { /* web / signed-out — description fallback applies */ });
    }, [refreshTrigger, currentUserId]);

    // The local→cloud playlist sync is fire-and-forget in the main process, so
    // the refresh dispatched at create/add time races it and can cache the
    // twin's stale "0 tracks" / missing cover. Refetch when the sync lands.
    useEffect(() => {
        const off = window.electron?.db?.playlists?.onCloudSynced?.(() => {
            dispatch(triggerPlaylistRefresh());
        });
        return off;
    }, [dispatch]);

    // Handle sidebar resize
    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            // The expanded panel sits right of the icon rail (position:fixed;
            // left:52px) — width is cursor minus its left edge, not raw clientX.
            const left = expandedRef.current?.getBoundingClientRect().left ?? 0;
            const newWidth = e.clientX - left;
            if (newWidth >= PLAYLIST_SIDEBAR_MIN_WIDTH && newWidth <= PLAYLIST_SIDEBAR_MAX_WIDTH) {
                dispatch(setSidebarWidth(newWidth));
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            persistPlaylistSidebarWidth(widthRef.current);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, dispatch]);

    const useServer = !!accessToken;

    const loadPlaylists = async () => {
        // Hydrate from a per-user cache (when present + fresh + matches the
        // current user) so the sidebar paints instantly on open and on user
        // re-login. The server fetch still fires in parallel below and
        // replaces whatever the cache surfaced. Anything stale from a
        // previous user is dropped up front — the key is namespaced by
        // userId, so a mismatch just misses and we fall through to fetch.
        let hydratedFromCache = false;
        if (useServer && currentUserId) {
            const cached = readPlaylistsCache(currentUserId);
            if (cached) {
                dispatch(setPlaylists(cached));
                hydratedFromCache = true;
            } else {
                // No cache hit for THIS user — make sure the previous user's
                // array isn't sitting in Redux while we wait for the network.
                dispatch(setPlaylists([]));
            }
        } else if (useServer) {
            // Logged in but user.id not yet hydrated — clear so we don't
            // surface the previous account's playlists during the gap.
            dispatch(setPlaylists([]));
        }

        try {
            // Only show the spinner if the cache didn't already paint
            // something — otherwise we'd flicker stale → spinner → fresh.
            if (!hydratedFromCache) dispatch(setLoading(true));
            if (useServer) {
                // refreshAndRetry: a cold-start 401 (restored token already
                // expired) refreshes and retries instead of failing silently.
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                if (result?.data) {
                    const normalized = result.data.map(p => ({
                        ...p,
                        trackCount: p.track_count,
                    }));
                    dispatch(setPlaylists(normalized));
                    if (currentUserId) writePlaylistsCache(currentUserId, normalized);
                } else {
                    // Failed fetch ({error} from a cold-start 401 before the
                    // token refresh, or network not up yet) — NOT an empty
                    // account (that's data: []). Keep whatever's on screen
                    // (usually the cache) and never write [] into the
                    // per-user cache: poisoning it makes the NEXT launch
                    // paint an empty sidebar too. loadPlaylists re-runs on
                    // token refresh / refreshTrigger, which heals this.
                    console.warn('[PlaylistSidebar] getPlaylists failed, keeping current list:', result?.error);
                    if (!hydratedFromCache) dispatch(setPlaylists([]));
                }
            } else if (isDesktop) {
                const allPlaylists = await window.electron.db.playlists.getAll(true);
                dispatch(setPlaylists(allPlaylists));
            } else {
                dispatch(setPlaylists([]));
            }
        } catch (error) {
            // Cache may already be on screen — only blank it if we hadn't
            // hydrated; otherwise keep the cached array visible while the
            // user retries.
            if (!hydratedFromCache) dispatch(setPlaylists([]));
        } finally {
            dispatch(setLoading(false));
        }
    };

    const processImageFile = (file) => {
        // Check file size (limit to 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.warning('Image size should be less than 2MB');
            return;
        }

        // Check file type
        if (!file.type.startsWith('image/')) {
            toast.warning('Please select an image file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setCoverImage(event.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            processImageFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processImageFile(files[0]);
        }
    };

    const handleCreatePlaylist = async () => {
        if (!newPlaylistName.trim()) {
            toast.warning('Please enter a playlist name');
            return;
        }

        try {
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.createPlaylist(token, {
                    name: newPlaylistName,
                    description: newPlaylistDescription,
                    cover_image: coverImage
                }));
                if (result?.error) { toast.error(result.error); return; }
                const fullPlaylist = result?.data || result;
                dispatch(setPlaylists([fullPlaylist, ...playlists]));
                dispatch(setSelectedPlaylist(fullPlaylist.id));
                setNewPlaylistName('');
                setNewPlaylistDescription('');
                setCoverImage(null);
                setShowCreateModal(false);
                toast.success(`Created playlist "${fullPlaylist.name}"`);
                trackEvent('playlist_created', { track_count: 0, from: 'sidebar' });
            } else if (isDesktop) {
                let playlist = await window.electron.db.playlists.create(
                    newPlaylistName,
                    newPlaylistDescription
                );

                // Add cover image if provided
                if (coverImage) {
                    playlist = await window.electron.db.playlists.update(playlist.id, {
                        coverImage: coverImage
                    });
                }

                // Add selected tracks if any
                if (selectedTracks.length > 0) {
                    const filePaths = selectedTracks.map(t => t.file || t.filePath);
                    await window.electron.db.playlists.addTracks(playlist.id, filePaths);
                    dispatch(clearSelection());
                }

                // Get the full playlist with tracks to add to Redux state
                const fullPlaylist = await window.electron.db.playlists.get(playlist.id, true);

                // Add the new playlist to Redux state
                dispatch(setPlaylists([fullPlaylist, ...playlists]));

                setNewPlaylistName('');
                setNewPlaylistDescription('');
                setCoverImage(null);
                setShowCreateModal(false);

                // Select the newly created playlist
                dispatch(setSelectedPlaylist(fullPlaylist.id));

                trackEvent('playlist_created', { track_count: selectedTracks.length, from: 'sidebar' });

                // Show appropriate success message
                if (selectedTracks.length > 0) {
                    toast.success(`Created playlist "${fullPlaylist.name}" with ${selectedTracks.length} track${selectedTracks.length !== 1 ? 's' : ''}`);
                } else {
                    toast.success(`Created playlist "${fullPlaylist.name}". Right-click tracks in the Library tab to add them.`);
                }
            }
        } catch (error) {
            console.error('Error creating playlist:', error);
            const errorMessage = error.message || error.toString();
            toast.error(`Failed to create playlist: ${errorMessage}`);
        }
    };

    const handleRenamePlaylist = async () => {
        if (!newPlaylistName.trim() || !editingPlaylist) {
            return;
        }

        try {
            let updatedPlaylist;
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.updatePlaylist(token, editingPlaylist.id, {
                    name: newPlaylistName,
                    description: newPlaylistDescription,
                    cover_image: coverImage
                }));
                if (result?.error) { toast.error(result.error); return; }
                updatedPlaylist = result?.data || result;
            } else if (isDesktop) {
                updatedPlaylist = await window.electron.db.playlists.update(editingPlaylist.id, {
                    name: newPlaylistName,
                    description: newPlaylistDescription,
                    coverImage: coverImage
                });
            }

            const updatedPlaylists = playlists.map(p =>
                p.id === editingPlaylist.id ? { ...p, ...updatedPlaylist } : p
            );
            dispatch(setPlaylists(updatedPlaylists));

            setShowRenameModal(false);
            setEditingPlaylist(null);
            setNewPlaylistName('');
            setNewPlaylistDescription('');
            setCoverImage(null);
        } catch (error) {
            console.error('Error renaming playlist:', error);
            toast.error('Failed to rename playlist');
        }
    };

    const handleDeletePlaylist = async (playlistId, playlistName) => {
        const confirmed = await toast.confirm(
            `Are you sure you want to delete "${playlistName}"?`,
            'Delete Playlist'
        );
        if (!confirmed) {
            return;
        }

        try {
            if (useServer) {
                await refreshAndRetry((token) => window.electron.library.deletePlaylist(token, playlistId));
            } else if (isDesktop) {
                await window.electron.db.playlists.delete(playlistId);
            }

            const updatedPlaylists = playlists.filter(p => p.id !== playlistId);
            dispatch(setPlaylists(updatedPlaylists));
            _expandedTracksCache.delete(playlistId);

            if (selectedPlaylistId === playlistId) {
                dispatch(setSelectedPlaylist(null));
            }
        } catch (error) {
            console.error('Error deleting playlist:', error);
            toast.error('Failed to delete playlist');
        }
    };

    // ── Drag-to-reorder (desktop + web; touch phones keep swipe actions) ──
    const dndSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    // A filtered view hides the rows a drop would land between, so what the
    // user sees is not the order they'd be saving — reordering waits for a
    // cleared search.
    const canReorder = !isTouchPhone && !!expandedPlaylistId && expandedPlaylistTracks.length > 1
        && (useServer || isDesktop) && !playlistFilter.trim();

    const handleTrackDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id || !expandedPlaylistId) return;
        const ids = expandedPlaylistTracks.map(sortableIdOf);
        const from = ids.indexOf(active.id);
        const to = ids.indexOf(over.id);
        if (from < 0 || to < 0) return;
        const prev = expandedPlaylistTracks;
        const next = arrayMove(prev, from, to);
        const playlistId = expandedPlaylistId;
        cacheExpandedTracks(playlistId, next); // optimistic
        const orderedFilePaths = next.map(t => t.filePath).filter(Boolean);
        try {
            if (useServer) {
                const rowIds = next.map(t => t.id).filter(id => id != null);
                const res = await refreshAndRetry((token) => window.electron.library.reorderPlaylistTracks(token, playlistId, rowIds, orderedFilePaths));
                if (res?.error) throw new Error(typeof res.error === 'string' ? res.error : 'reorder failed');
            } else if (isDesktop) {
                await window.electron.db.playlists.reorder(playlistId, orderedFilePaths);
            }
            trackEvent('playlist_tracks_reordered', { from: 'playlist_sidebar', source: useServer ? 'cloud' : 'local', track_count: next.length, moved_from: from, moved_to: to });
        } catch (err) {
            console.error('Reorder failed:', err);
            cacheExpandedTracks(playlistId, prev); // revert
            toast.error('Could not save the new order');
        }
    };

    // shapeServerTracks (shared with the local-file tools' pickers) lives in
    // utils/cloudPlaylistTracks — exact link/path resolution first, one-time
    // heuristic backfill for legacy rows.
    const fetchPlaylistTracks = async (playlistId) => {
        if (useServer) {
            const result = await refreshAndRetry((token) => window.electron.library.getPlaylist(token, playlistId));
            const playlistData = result?.data || result;
            if (!playlistData || playlistData.error) return null;
            const shaped = await shapeServerTracks(playlistData, { accessToken });
            // A name-only row with no sample renders as "on another device"
            // even when the catalog knows the track and it streams fine. Match
            // just those rows now so the badge, the hero count and the Play All
            // toast aren't lying before the user clicks anything.
            return upgradeLegacyRows(shaped, { playlistId, accessToken, onlyUnplayable: true });
        }
        if (isDesktop) {
            const playlistData = await window.electron.db.playlists.getForPlayer(playlistId);
            return playlistData?.tracks || null;
        }
        return null;
    };

    // Every write of fresh rows goes through here so the re-entry cache can
    // never go stale relative to what the user last saw.
    const cacheExpandedTracks = (playlistId, tracks) => {
        if (playlistId) _expandedTracksCache.set(playlistId, tracks);
        setExpandedPlaylistTracks(tracks);
    };

    // "Restore from Cloud Backup" for rows synced from another machine whose
    // file isn't on this one. Per-track (⋯ menu) and per-playlist (hero
    // missing-note action) share this path; the main process verifies every
    // download against its stored sha256 before a file is put in place, and
    // prompts for a folder only when the original directory is gone.
    const [restoringMissing, setRestoringMissing] = useState(false);
    const canRestoreFromBackup = isDesktop && !!window.electron?.backup?.restoreFiles;
    const restoreMissingFromBackup = async (items, from) => {
        if (!canRestoreFromBackup || !items.length || restoringMissing) return;
        setRestoringMissing(true);
        try {
            const res = await restoreFilesWithPrompt(items);
            if (!res || res.cancelled) return;
            if (res.error) {
                toast.error(res.error);
                return;
            }
            const restored = res.restored?.length || 0;
            const failed = res.failed?.length || 0;
            if (restored > 0) {
                trackEvent('backup_file_restored', { count: restored, from });
                // Re-shape the playlist so the badges and hero count clear.
                const tracks = await fetchPlaylistTracks(expandedPlaylistId).catch(() => null);
                if (tracks) cacheExpandedTracks(expandedPlaylistId, tracks);
            }
            if (restored > 0 && failed === 0) {
                toast.success(`Restored ${restored} ${restored === 1 ? 'file' : 'files'} from Cloud Backup`);
            } else if (restored > 0) {
                toast.warning(`Restored ${restored} ${restored === 1 ? 'file' : 'files'} — ${failed} couldn't be restored`);
            } else if ((res.skipped || 0) > 0) {
                toast.info('These files are already on this device');
            } else {
                toast.error(failed === 1 ? "This track isn't in your Cloud Backup" : "These tracks aren't in your Cloud Backup");
            }
        } finally {
            setRestoringMissing(false);
        }
    };

    const handleLoadPlaylist = async (playlistId, append = false, { push = true } = {}) => {
        // If already expanded, collapse it
        if (expandedPlaylistId === playlistId) {
            setExpandedPlaylistId(null);
            setExpandedPlaylistTracks([]);
            return;
        }
        // Design v2: opening a playlist is a navigation stop on top of the list,
        // so Back returns to the list (history moves arrive with push: false).
        if (push && designV2 && !asPage) {
            dispatch(pushNavEntry({ category: activeCategory, tab: activeTab, panel: 'playlists', playlist: playlistId }));
        }

        // Expand. Cached rows paint immediately with the hero (no spinner,
        // no empty flash) and the fetch below silently replaces them; a
        // first-ever open gets the spinner.
        const cached = _expandedTracksCache.get(playlistId);
        setExpandedPlaylistId(playlistId);
        setExpandedPlaylistLoading(!cached);
        setExpandedPlaylistTracks(cached || []);

        try {
            const tracks = await fetchPlaylistTracks(playlistId);
            if (tracks === null) {
                if (!cached) {
                    toast.error('Playlist not found');
                    setExpandedPlaylistId(null);
                }
                return;
            }
            cacheExpandedTracks(playlistId, tracks);
        } catch (error) {
            console.error('Error loading playlist:', error);
            if (!cached) {
                toast.error('Failed to load playlist');
                setExpandedPlaylistId(null);
            }
        } finally {
            setExpandedPlaylistLoading(false);
        }
    };

    // Mirror the open playlist so the next mount can land back on it.
    useEffect(() => { setLastExpandedPlaylistId(expandedPlaylistId); }, [expandedPlaylistId]);

    // A search belongs to the playlist it was typed in.
    useEffect(() => { setPlaylistFilter(''); playlistFilterFiredRef.current = false; }, [expandedPlaylistId]);

    // requestOpenPlaylist() from elsewhere → expand that playlist here (no-op
    // if it's already the open one; handleLoadPlaylist would collapse it).
    const openRequestRef = useRef(null);
    openRequestRef.current = (id, push) => {
        if (id == null) { if (expandedPlaylistId) { setExpandedPlaylistId(null); setExpandedPlaylistTracks([]); } return; }
        if (expandedPlaylistId !== id) handleLoadPlaylist(id, false, { push: push !== false });
    };
    useEffect(() => {
        const onOpen = (e) => { const d = e.detail || {}; if ('playlistId' in d) openRequestRef.current(d.playlistId, d.push); };
        window.addEventListener(OPEN_PLAYLIST_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_PLAYLIST_EVENT, onOpen);
    }, []);

    // A remembered playlist that no longer exists (deleted from another device,
    // or a different account signed in) would restore into a permanently empty
    // view with no way back but the header arrow. Once the list has actually
    // loaded, drop the memory and show the list instead.
    useEffect(() => {
        if (!expandedPlaylistId || !playlists?.length) return;
        if (!playlists.some(p => p.id === expandedPlaylistId)) {
            setLastExpandedPlaylistId(null);
            setExpandedPlaylistId(null);
        }
    }, [playlists, expandedPlaylistId]);

    // The guard above needs a non-empty list to fire, which a fresh account
    // never provides — so on an account switch while mounted, collapse the
    // previous user's expanded view (and its already-painted rows) directly.
    const expandedOwnerRef = useRef(currentUserId);
    useEffect(() => {
        if (expandedOwnerRef.current === currentUserId) return;
        expandedOwnerRef.current = currentUserId;
        setLastExpandedPlaylistId(null);
        setExpandedPlaylistId(null);
        setExpandedPlaylistTracks([]);
    }, [currentUserId]);

    // Re-fetch the expanded playlist's tracks when something elsewhere mutates
    // it (e.g. AddToPlaylistModal dispatches triggerPlaylistRefresh after a
    // successful add). Without this, the expanded view shows stale data until
    // the user navigates away and back.
    useEffect(() => {
        if (!expandedPlaylistId) return;
        let cancelled = false;
        (async () => {
            try {
                const tracks = await fetchPlaylistTracks(expandedPlaylistId);
                if (!cancelled && tracks) cacheExpandedTracks(expandedPlaylistId, tracks);
            } catch (_) {
                // Silent — the user's main view (the sidebar list) refreshes
                // independently via loadPlaylists; surfacing a toast here on a
                // background reload would be noisy.
            } finally {
                // Also the mount-restore path: state init showed the spinner
                // when a restored playlist had no cached rows yet.
                if (!cancelled) setExpandedPlaylistLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger]);

    // Play All is one action per click: the await below leaves the handler
    // re-entrant, and a stray second click (or a duplicated click event —
    // reported on Windows) would queue the playlist twice and stack two
    // identical toasts.
    const playAllBusyRef = useRef(false);
    // Button feedback: spinner from the click until the player actually
    // starts (legacy-row upgrade + queue swap can take seconds), then the
    // queueOwner marker flips the label to Pause/Resume while THIS playlist
    // owns the queue. Same three-state contract as the likes Play All.
    const [playAllStarting, setPlayAllStarting] = useState(false);
    // Post-start grace: the native playerState stream briefly dips
    // isPlaying=false while a cold-started queue buffers, flickering the
    // button Pause → Resume → Pause right after the spinner clears (first
    // click only, mobile). Hold Pause through the dips; a real tap ends the
    // grace immediately. Same fix as PlayAllButton.
    const [playAllGrace, setPlayAllGrace] = useState(false);
    const playAllGraceTimerRef = useRef(null);
    useEffect(() => () => clearTimeout(playAllGraceTimerRef.current), []);
    const ownsQueue = queueOwner?.type === 'playlist' && queueOwner.id === expandedPlaylistId;
    const showPause = ownsQueue && (isPlaying || playAllGrace);

    // Force the player back to the first queue entry. Only needed when the
    // queue we just dispatched is identical to the loaded one — the player
    // library re-inits (and starts at the top) on its own whenever the list
    // actually changes.
    const restartQueueFromTop = useCallback((queue) => {
        const first = queue?.[0];
        if (!first) return;
        let attempts = 0;
        const tryOnce = () => {
            attempts += 1;
            const audio = window.musicPlayerInstance;
            // Already on the first track: rewind rather than re-trigger it — a
            // panel click on the CURRENT item toggles play/pause in jinke. The
            // >1s guard keeps a successful jump (now at ~0.3s) from being
            // pointlessly reset by the follow-up attempt.
            if (window.__currentPlayerMusicSrc === first.musicSrc) {
                try {
                    if (audio && audio.currentTime > 1) {
                        audio.currentTime = 0;
                        audio.play()?.catch(() => {});
                    }
                } catch (_) { /* element not ready — the dispatch above stands */ }
                return;
            }
            // Mid-queue: click the first entry in jinke's hidden queue panel
            // (the same DOM route row clicks use — matched by content, never by
            // index). Retry a couple of times: the setAudioList dispatch above
            // re-inits the player's internal list a beat later and can land it
            // back where it was, undoing a jump made too early.
            const item = findJinkeQueueItem(queue, 0);
            if (item) item.click();
            if (attempts < 3) setTimeout(tryOnce, 400);
        };
        // Deferred: let React flush the dispatched queue into the player first.
        setTimeout(tryOnce, 0);
    }, []);

    // Pause/resume the playlist queue in place while we own it — clicking
    // "Pause"/"Resume" must NOT rebuild the queue from the top. Mirrors
    // handlePauseTrack's native-safe route.
    const handleTogglePlaylistPlayback = () => {
        // Deliberate tap → the shown state must be the real one from here on.
        clearTimeout(playAllGraceTimerRef.current);
        setPlayAllGrace(false);
        trackEvent('play_all_clicked', { from: 'playlist', action: isPlaying ? 'pause' : 'resume' });
        if (isNative) {
            const btn = document.querySelector('.react-jinke-music-player-main .player-content .play-btn');
            if (btn) btn.click();
            return;
        }
        const audio = window.musicPlayerInstance;
        if (!audio) return;
        if (audio.paused) audio.play().catch(() => {});
        else audio.pause();
    };

    const handlePlayAllPlaylist = async () => {
        if (playAllBusyRef.current) return;
        if (!expandedPlaylistTracks.length) return;
        playAllBusyRef.current = true;
        setPlayAllStarting(true);
        trackEvent('play_all_clicked', { from: 'playlist', action: 'start' });
        let started = false;
        try {
            const name = playlists.find(p => p.id === expandedPlaylistId)?.name || 'Playlist';
            // Legacy rows carry only artist/title/sample — queueing them as-is
            // played the 30-second Apple sample for a track that streams in
            // full when its row is clicked (the click path ran this match, Play
            // All didn't). Upgrade first, THEN decide what's playable: a match
            // also rescues rows that had no source at all.
            const tracks = await upgradeLegacyRows(
                expandedPlaylistTracks,
                useServer ? { playlistId: expandedPlaylistId, accessToken } : {}
            );
            cacheExpandedTracks(expandedPlaylistId, tracks);
            // Keep tracks that aren't on this device out of the queue so playback
            // doesn't 404-cascade through them (which floods errors and hangs the
            // player's spinner). Surface a single, clear explanation instead.
            const total = tracks.length;
            const playable = tracks.filter(t => !t.missingLocal && t.musicSrc);
            const missing = total - playable.length;
            if (!playable.length) {
                toast.warning(`None of the tracks in "${name}" are on this device — they're on the device you synced them from.`, 7000);
                return;
            }
            // See handlePlayTrackFromPlaylist: the queue is this playlist now,
            // so any stale list context must go with it.
            dispatch(clearListContext());
            dispatch(setAudioList(playable));
            dispatch(setAutoPlay(true));
            // Play All means "start this playlist from the top" — always. When
            // the dispatched queue is identical to the loaded one (re-clicking
            // Play All on the playlist that's already playing) the player sees
            // no change and keeps going mid-queue, which reads as a dead
            // button. This is a no-op when the player already lands on track 1.
            restartQueueFromTop(playable);
            // Claim the queue AFTER setAudioList (which clears any owner) so
            // the button's Pause/Resume state is honest from here on.
            dispatch(setQueueOwner({ type: 'playlist', id: expandedPlaylistId }));
            started = true;
            // Hold the spinner until the player actually starts — clearing on
            // dispatch flashes the idle label back for the beat before
            // onAudioPlay fires (see the likes Play All for the same fix).
            const clearStarting = () => {
                setPlayAllStarting(false);
                setPlayAllGrace(true);
                clearTimeout(playAllGraceTimerRef.current);
                playAllGraceTimerRef.current = setTimeout(() => setPlayAllGrace(false), 1600);
                window.removeEventListener('player-track-change', onPlaying);
            };
            const onPlaying = (e) => { if (e.detail?.playing) clearStarting(); };
            window.addEventListener('player-track-change', onPlaying);
            setTimeout(clearStarting, 12000);
            // No "Playing X" confirmation: the player itself is the feedback,
            // and it appears at the same moment. Only speak up when the queue
            // is NOT what the user asked for — some tracks had to be left out.
            if (missing > 0) {
                toast.info(`Playing "${name}" — ${missing} of ${total} tracks ${missing === 1 ? "isn't" : "aren't"} on this device.`, 7000);
            }
        } finally {
            if (!started) setPlayAllStarting(false);
            // Short cooldown rather than an immediate release: the duplicate
            // click arrives after this run finishes, not during it.
            setTimeout(() => { playAllBusyRef.current = false; }, 600);
        }
    };

    const handleLoadIntoPlayerQueue = async (playlistId, append = false) => {
        try {
            let playlistData;
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylist(token, playlistId));
                playlistData = result?.data || result;
                if (playlistData?.error) playlistData = null;
                if (playlistData?.tracks) {
                    // Shared shaping: resolves local files and sets missingLocal
                    // so the not-on-this-device filter below actually works for
                    // server playlists.
                    playlistData.tracks = await shapeServerTracks(playlistData, { accessToken });
                }
            } else if (isDesktop) {
                playlistData = await window.electron.db.playlists.getForPlayer(playlistId);
            }
            if (!playlistData) { toast.error('Playlist not found'); return; }
            dispatch(setSelectedPlaylist(playlistId));
            dispatch(setSelectedPlaylistName(playlistData.name));
            if (!playlistData.tracks || playlistData.tracks.length === 0) {
                dispatch(setAudioList([]));
                toast.info('This playlist is empty.');
                return;
            }
            // Same legacy-row upgrade Play All does — without it this queue
            // gets the 30-second samples (see handlePlayAllPlaylist).
            playlistData.tracks = await upgradeLegacyRows(playlistData.tracks);
            // Leave out tracks whose files aren't on this device so the queue
            // doesn't cascade through 404s (see handlePlayAllPlaylist).
            const total = playlistData.tracks.length;
            const queueTracks = playlistData.tracks.filter(t => !t.missingLocal && t.musicSrc);
            const missing = total - queueTracks.length;
            if (!queueTracks.length) {
                toast.warning(`None of the tracks in "${playlistData.name}" are on this device — they're on the device you synced them from.`, 7000);
                return;
            }
            if (append) {
                dispatch(setAudioList([...audioList, ...queueTracks]));
            } else {
                // Wholesale replace — drop any stale list context with the
                // old queue (see handlePlayTrackFromPlaylist). Append keeps
                // it: the ambient list legitimately resumes after the
                // appended tracks play out.
                dispatch(clearListContext());
                dispatch(setAudioList(queueTracks));
            }
            const missingNote = missing > 0 ? ` (${missing} not on this device)` : '';
            toast.info(`${append ? 'Added' : 'Loaded'} "${playlistData.name}" to player queue${missingNote}`);
        } catch (error) {
            console.error('Error loading playlist:', error);
            toast.error('Failed to load playlist');
        }
    };



    // Load or append playlist tracks to a specific tab
    const handleOpenInTab = async (playlistId, tabName, append = false) => {
        if (isWeb) { toast.info('This feature is only available in the desktop app'); return; }
        setContextMenu(null); // Close menu first
        setOpenDropdownId(null); // Close dropdown

        try {
            // Smart Recs only needs the playlist id (its thunk fetches cloud or
            // local itself) — handle it before any DB fetch so cloud playlists
            // don't miss on the local lookup below.
            if (tabName === 'smart-recs') {
                const plName = playlists.find(p => p.id === playlistId)?.name;
                dispatch(setSmartRecsPlaylist(playlistId));
                dispatch(clearSmartRecsResults());
                dispatch(setActiveTab('smart-recs'));
                dispatch(fetchPlaylistRecommendations({ playlistId, variant: 'A' }));
                toast.info(`Getting recommendations for "${plName || 'playlist'}"...`);
                return;
            }

            let playlistData = null;
            let filePaths = [];
            let totalTracks = 0;
            if (useServer) {
                // Cloud playlist — resolve rows to local files (exact link /
                // origin path, one-time heuristic backfill for legacy rows).
                const loaded = await loadCloudPlaylistFiles(accessToken, playlistId);
                if (!loaded) {
                    toast.warning('Playlist not found');
                    return;
                }
                playlistData = { name: loaded.name, tracks: [] };
                totalTracks = loaded.total;
                if (loaded.total === 0) {
                    toast.warning('This playlist has no tracks');
                    return;
                }
                filePaths = loaded.filePaths;
                if (filePaths.length === 0) {
                    toast.warning('None of this playlist’s tracks are on this device — these tools work on local audio files');
                    return;
                }
            } else {
                playlistData = await window.electron.db.playlists.get(playlistId, true);

                if (!playlistData) {
                    toast.warning('Playlist not found');
                    return;
                }

                if (!playlistData.tracks || playlistData.tracks.length === 0) {
                    toast.warning('This playlist has no tracks');
                    return;
                }

                // Get file paths from playlist tracks
                const allFilePaths = playlistData.tracks
                    .map(track => track.file_path || track.filePath)
                    .filter(path => path);

                if (allFilePaths.length === 0) {
                    toast.warning('No valid file paths found in playlist');
                    return;
                }

                // For tabs that work with local files only, filter out cloud/online tracks
                // The Library (visualize) tab handles cloud tracks in its own UI
                const isCloudPath = (f) => f.startsWith('online://') || f.startsWith('http://') || f.startsWith('https://');
                totalTracks = allFilePaths.length;
                filePaths = tabName === 'visualize'
                    ? allFilePaths
                    : allFilePaths.filter(f => !isCloudPath(f));

                if (filePaths.length === 0) {
                    toast.warning('This playlist only contains cloud tracks — no local files to work with');
                    return;
                }
            }

            // Get current files and merge if appending
            const getMergedFiles = (currentFiles) => {
                if (!append) return filePaths;
                // Filter out duplicates
                const existingSet = new Set(currentFiles);
                const newFiles = filePaths.filter(f => !existingSet.has(f));
                return [...currentFiles, ...newFiles];
            };

            // Set files in the correct slice based on the target tab
            switch (tabName) {
                case 'fix-my-tracks':
                    dispatch(setFiles(getMergedFiles(appFiles)));
                    dispatch(setAutoAnalyze(true));
                    break;
                case 'match-loudness':
                    dispatch(setLoudnessFiles(getMergedFiles(loudnessFiles)));
                    dispatch(setLoudnessAutoAnalyze(true));
                    break;
                case 'fix-metadata':
                    dispatch(setMetadataFiles(getMergedFiles(metadataFiles)));
                    dispatch(setMetadataAutoAnalyze(true));
                    break;
                case 'organize-tracks':
                    dispatch(setOrganizerFiles(getMergedFiles(organizerFiles)));
                    dispatch(setOrganizerAutoAnalyze(true));
                    break;
                case 'visualize': {
                    const mergedFiles = getMergedFiles(infoFiles);
                    dispatch(resetEnrichedFiles());
                    dispatch(setInfoFiles(mergedFiles));
                    // Pre-populate cloud tracks with DB metadata so they appear in the table
                    // playlistData.tracks are in enriched file format: { file, fileName, track: { artist, title, ... } }
                    const cloudTracks = (playlistData.tracks || []).filter(t => {
                        const fp = t.file || t.file_path || t.filePath || '';
                        return fp.startsWith('online://') || fp.startsWith('http://') || fp.startsWith('https://');
                    });
                    if (cloudTracks.length > 0) {
                        const enriched = cloudTracks.map(t => ({
                            file: t.file || t.file_path || t.filePath,
                            fileName: t.fileName || t.file_name || (t.file || '').split('/').pop(),
                            result: { track: t.track || {} }
                        }));
                        dispatch(addEnrichedFilesBatch(enriched));
                        dispatch(recomputeGraphData());
                    }
                    dispatch(setInfoPlaylistName(playlistData.name));
                    dispatch(setInfoAutoAnalyze(true));
                    break;
                }
                default:
                    dispatch(setFiles(getMergedFiles(appFiles)));
            }

            // Switch to the target tab
            dispatch(setActiveTab(tabName));

            const action = append ? 'Added' : 'Loaded';
            const skipped = totalTracks - filePaths.length;
            trackEvent('playlist_opened_in_tool', {
                tab: tabName,
                source: useServer ? 'cloud' : 'local',
                track_count: filePaths.length,
                skipped_count: skipped,
            });
            toast.info(`${action} ${filePaths.length} ${filePaths.length === 1 ? 'track' : 'tracks'} ${append ? 'to' : 'into'} ${tabName.replace(/-/g, ' ')}${skipped > 0 ? ` — skipped ${skipped} with no local file on this device` : ''}`);
        } catch (error) {
            console.error('Error opening playlist in tab:', error);
            toast.error('Failed to load playlist tracks: ' + errMsg(error));
        }
    };

    // Context menu handler
    const handleContextMenu = (e, playlist) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenuPlaylist(playlist);
        setContextMenuPos(null);
        setContextMenu({ x: e.clientX, y: e.clientY });
    };

    // Flip/clamp the playlist context menu so it stays on-screen near an edge.
    useLayoutEffect(() => {
        if (!contextMenu || !contextMenuRef.current) { setContextMenuPos(null); return; }
        setContextMenuPos(positionMenuAtPoint(contextMenuRef.current, contextMenu.x, contextMenu.y));
    }, [contextMenu, contextMenuPlaylist]);

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            // Check if click is inside the context menu
            if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) {
                return; // Don't close if clicking inside the menu
            }
            setContextMenu(null);
        };
        if (contextMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [contextMenu]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
                return;
            }
            setOpenDropdownId(null);
        };
        if (openDropdownId) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [openDropdownId]);

    // Decide whether the "Open in ▾" dropdown should flip above its button.
    // The decision only depends on the button's position + the menu's height,
    // so it's stable regardless of where the menu currently renders.
    useLayoutEffect(() => {
        if (!openDropdownId || !actionDropdownRef.current || !dropdownRef.current) {
            setOpenDropdownUp(false);
            return;
        }
        const btn = dropdownRef.current.querySelector('.open-in-btn');
        if (!btn) return;
        const { openUp } = positionMenuByTrigger(actionDropdownRef.current, btn.getBoundingClientRect(), { align: null });
        setOpenDropdownUp(openUp);
    }, [openDropdownId]);

    const handleSavePlayerQueue = async () => {
        if (!newPlaylistName.trim()) {
            toast.warning('Please enter a playlist name');
            return;
        }

        if (audioList.length === 0) {
            toast.warning('No tracks in player to save');
            return;
        }

        try {
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.createPlaylist(token, {
                    name: newPlaylistName,
                    description: newPlaylistDescription,
                    cover_image: coverImage
                }));
                if (result?.error) { toast.error(result.error); return; }
                const playlist = result?.data || result;
                // Add each track from the audio list
                for (const track of audioList) {
                    const localPath = localPathFromTrack(track);
                    const result = await refreshAndRetry((token) => window.electron.library.addTrackToPlaylist(token, playlist.id, {
                        artist: track.singer || track.artist || '',
                        title: track.name || track.title || '',
                        artwork: track.cover || track.artwork || '',
                        // Local files play via the localhost audio server — that
                        // URL is machine-specific, so send the real path instead.
                        // Queue entries (/lazy-audio) are localhost too: persist
                        // the REAL sample + catalog id/links so the saved row
                        // stays playable everywhere.
                        preview_url: localPath ? '' : (track.samplePreviewUrl || nonLocalhostUrl(track.musicSrc) || track.preview_url || ''),
                        trackPublicId: track.djpalId || track.track_public_id || track.publicId || undefined,
                        youtube_id: track.youtubeId && track.youtubeId !== '-1' ? track.youtubeId : undefined,
                        soundcloud_url: track.soundcloudUrl || undefined,
                        file_path: localPath || '',
                    }));
                    // Seed the cloud↔local link so the first playlist load is
                    // already exact (row id comes back on create AND duplicate).
                    const rowId = result?.data?.id || result?.data?.track?.id;
                    if (localPath && rowId) {
                        window.electron.db?.cloudLinks?.setByPath?.(rowId, localPath)?.catch?.(() => {});
                    }
                }
                dispatch(setPlaylists([{ ...playlist, trackCount: audioList.length }, ...playlists]));
                dispatch(setSelectedPlaylist(playlist.id));
                setNewPlaylistName('');
                setNewPlaylistDescription('');
                setCoverImage(null);
                dispatch(setShowSavePlayerModal(false));
                toast.success(`Saved ${audioList.length} track${audioList.length !== 1 ? 's' : ''} to playlist "${playlist.name}"`);
                trackEvent('playlist_created', { track_count: audioList.length, from: 'player_queue' });
            } else if (isDesktop) {
                let playlist = await window.electron.db.playlists.createFromPlayer(
                    newPlaylistName,
                    audioList
                );

                if (newPlaylistDescription.trim() || coverImage) {
                    playlist = await window.electron.db.playlists.update(playlist.id, {
                        description: newPlaylistDescription,
                        coverImage: coverImage
                    });
                }

                const fullPlaylist = await window.electron.db.playlists.get(playlist.id, true);

                dispatch(setPlaylists([fullPlaylist, ...playlists]));

                setNewPlaylistName('');
                setNewPlaylistDescription('');
                setCoverImage(null);
                dispatch(setShowSavePlayerModal(false));
                dispatch(setSelectedPlaylist(fullPlaylist.id));
                toast.success(`Saved ${audioList.length} track${audioList.length !== 1 ? 's' : ''} to playlist "${fullPlaylist.name}"`);
                trackEvent('playlist_created', { track_count: audioList.length, from: 'player_queue' });
            }
        } catch (error) {
            console.error('Error saving player queue:', error);
            const errorMessage = error.message || error.toString();
            toast.error(`Failed to save player queue: ${errorMessage}`);
        }
    };

    const handleAddTracksToPlaylist = async (playlistId) => {
        if (selectedTracks.length === 0) {
            toast.warning('No tracks selected');
            return;
        }

        try {
            if (useServer) {
                let added = 0;
                for (const track of selectedTracks) {
                    const localPath = localPathFromTrack(track);
                    const result = await refreshAndRetry((token) => window.electron.library.addTrackToPlaylist(token, playlistId, {
                        artist: track.artist || track.singer || '',
                        title: track.title || track.name || '',
                        artwork: track.cover || track.artwork || '',
                        // Local files: real path (portable) instead of the
                        // machine-specific localhost audio-server URL. Same for
                        // /lazy-audio queue entries — persist the REAL sample
                        // + catalog id/links, never a localhost URL.
                        preview_url: localPath ? '' : (track.samplePreviewUrl || nonLocalhostUrl(track.musicSrc) || track.preview_url || ''),
                        trackPublicId: track.djpalId || track.track_public_id || track.publicId || undefined,
                        youtube_id: track.youtubeId && track.youtubeId !== '-1' ? track.youtubeId : undefined,
                        soundcloud_url: track.soundcloudUrl || undefined,
                        file_path: localPath || '',
                    }));
                    if (!result?.alreadyExists && !result?.data?.alreadyExists) added++;
                    // Seed the cloud↔local link (row id comes back on create
                    // AND duplicate) so the first load resolves exactly.
                    const rowId = result?.data?.id || result?.data?.track?.id;
                    if (localPath && rowId) {
                        window.electron.db?.cloudLinks?.setByPath?.(rowId, localPath)?.catch?.(() => {});
                    }
                }
                toast.success(`Added ${added} track${added !== 1 ? 's' : ''} to playlist`);
                dispatch(clearSelection());
                loadPlaylists(); // Refresh to get updated track counts
            } else if (isDesktop) {
                const filePaths = selectedTracks.map(t => t.file || t.filePath);
                const result = await window.electron.db.playlists.addTracks(playlistId, filePaths);

                toast.success(`Added ${result.added} track${result.added !== 1 ? 's' : ''} to playlist${result.failed > 0 ? ` (${result.failed} already existed)` : ''}`);
                dispatch(clearSelection());

                const updatedPlaylist = await window.electron.db.playlists.get(playlistId, true);
                const updatedPlaylists = playlists.map(p =>
                    p.id === playlistId ? updatedPlaylist : p
                );
                dispatch(setPlaylists(updatedPlaylists));
            }
        } catch (error) {
            console.error('Error adding tracks to playlist:', error);
            toast.error('Failed to add tracks to playlist');
        }
    };

    const openRenameModal = (playlist) => {
        setEditingPlaylist(playlist);
        setNewPlaylistName(playlist.name);
        setNewPlaylistDescription(playlist.description || '');
        setCoverImage(playlist.cover_image || playlist.coverImage || null);
        setShowRenameModal(true);
    };

    // Search also matches tracks INSIDE playlists. Server-side first (cloud
    // twins know every synced playlist's tracks, including ones imported on
    // other machines), supplemented on desktop by the local DB so untagged
    // local files (file-name-only) still match. Results render as a separate
    // "Containing ..." group below the name matches, never silently mixed in.
    const [trackMatches, setTrackMatches] = useState(null); // { byId: Map, byName: Map }
    useEffect(() => {
        const q = searchQuery.trim();
        // 3+ chars: at 2 ("dj") the content search matches half the library
        // and the "Containing" group drowns out the name matches.
        if (q.length < 3) { setTrackMatches(null); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            const byId = new Map();
            const byName = new Map();
            const norm = (m) => ({ firstMatch: m.first_match || m.firstMatch || '', matchCount: m.match_count ?? m.matchCount ?? 1 });
            const jobs = [];
            if (useServer && window.electron?.library?.searchPlaylistsByTrack) {
                jobs.push(refreshAndRetry((token) => window.electron.library.searchPlaylistsByTrack(token, q))
                    .then((res) => { for (const m of res?.data || []) byId.set(m.id, norm(m)); })
                    .catch(() => {}));
            }
            if (window.electron?.db?.playlists?.searchByTrack) {
                jobs.push(window.electron.db.playlists.searchByTrack(q)
                    .then((rows) => { for (const m of rows || []) byName.set((m.name || '').trim().toLowerCase(), norm(m)); })
                    .catch(() => {}));
            }
            await Promise.all(jobs);
            if (!cancelled) setTrackMatches(byId.size || byName.size ? { byId, byName } : null);
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, useServer]);

    const nameMatchesQuery = (p) => p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const trackMatchFor = (p) => {
        if (!trackMatches || searchQuery.trim().length < 3) return null;
        return trackMatches.byId.get(p.id) || trackMatches.byName.get((p.name || '').trim().toLowerCase()) || null;
    };
    // Source chips compose with the text search: chip narrows the universe,
    // query searches within it. Connections are OR-filters (a playlist synced
    // with Serato AND Traktor matches both chips, so DJ counts may sum over
    // the total); 'mixgoat' = cloud-only rows with no DJ connection at all.
    // No "All" chip — Spotify-style: nothing selected means everything, the
    // active chip clears on re-tap. Only buckets that EXIST get a chip, and
    // fewer than two buckets means no filter row at all (nothing to choose).
    const sourceLinks = sourceLinksMaps ? (useServer ? sourceLinksMaps.byServerId : sourceLinksMaps.byLocalId) : null;
    const sourceCounts = connectionCounts(playlists, sourceLinks);
    const visibleSourceChips = ['mixgoat', ...CONNECTION_APPS].filter(s => sourceCounts[s] > 0);
    // A filter whose chip vanished (e.g. its last playlist was removed) must
    // not strand the user on an empty, unclearable list — treat it as off.
    const activeSource = visibleSourceChips.includes(sourceFilter) ? sourceFilter : 'all';
    const filteredPlaylists = playlists.filter(p =>
        (activeSource === 'all' || matchesConnection(p, sourceLinks, activeSource))
        && (nameMatchesQuery(p) || !!trackMatchFor(p)));

    // The open playlist's rows, narrowed by the in-playlist search. Each pair
    // keeps the track's ORIGINAL index: the row's sortable id is derived from
    // it, and clicking a row still queues the WHOLE playlist from that track
    // (handlePlayTrackFromPlaylist works off expandedPlaylistTracks, so a
    // filtered view narrows what you SEE, never what you play).
    const rowSearchText = (t) => `${cleanDisplayTitle(t.name) || t.name || ''} ${t.singer || ''}`;
    const visibleTrackRows = expandedPlaylistTracks
        .map((track, idx) => ({ track, idx }))
        .filter(({ track }) => matchesQuery(rowSearchText(track), playlistFilter));
    // A search box over a handful of rows is noise — it shows up once reading
    // the list by eye stops being realistic.
    const showPlaylistFilter = !expandedPlaylistLoading && expandedPlaylistTracks.length > 5;
    const handlePlaylistFilterChange = (value) => {
        setPlaylistFilter(value);
        if (value && !playlistFilterFiredRef.current) {
            playlistFilterFiredRef.current = true;
            trackEvent('playlist_tracks_searched', {
                track_count: expandedPlaylistTracks.length,
                source: useServer ? 'cloud' : 'local',
            });
        }
    };

    const handleOpenLikesSidebar = () => {
        if (sidebarOpen) dispatch(setSidebarOpen(false));
        if (rekordboxSidebarOpen) dispatch(setRekordboxSidebarOpen(false));
        if (seratoSidebarOpen) dispatch(setSeratoSidebarOpen(false));
        if (traktorSidebarOpen) dispatch(setTraktorSidebarOpen(false));
        if (virtualdjSidebarOpen) dispatch(setVirtualDJSidebarOpen(false));
        if (likedMixesSidebarOpen) dispatch(setLikedMixesSidebarOpen(false));
        if (followedArtistsSidebarOpen) dispatch(setFollowedArtistsSidebarOpen(false));
        dispatch(toggleLikesSidebar());
    };

    const handleOpenLikedMixesSidebar = () => {
        if (sidebarOpen) dispatch(setSidebarOpen(false));
        if (likesSidebarOpen) dispatch(setLikesSidebarOpen(false));
        if (rekordboxSidebarOpen) dispatch(setRekordboxSidebarOpen(false));
        if (seratoSidebarOpen) dispatch(setSeratoSidebarOpen(false));
        if (traktorSidebarOpen) dispatch(setTraktorSidebarOpen(false));
        if (virtualdjSidebarOpen) dispatch(setVirtualDJSidebarOpen(false));
        if (followedArtistsSidebarOpen) dispatch(setFollowedArtistsSidebarOpen(false));
        dispatch(toggleLikedMixesSidebar());
    };

    const handleOpenPlaylistSidebar = () => {
        if (likesSidebarOpen) dispatch(setLikesSidebarOpen(false));
        if (likedMixesSidebarOpen) dispatch(setLikedMixesSidebarOpen(false));
        if (followedArtistsSidebarOpen) dispatch(setFollowedArtistsSidebarOpen(false));
        if (rekordboxSidebarOpen) dispatch(setRekordboxSidebarOpen(false));
        if (seratoSidebarOpen) dispatch(setSeratoSidebarOpen(false));
        if (traktorSidebarOpen) dispatch(setTraktorSidebarOpen(false));
        if (virtualdjSidebarOpen) dispatch(setVirtualDJSidebarOpen(false));
        dispatch(toggleSidebar());
    };

    const handleOpenRekordboxSidebar = () => {
        if (sidebarOpen) dispatch(setSidebarOpen(false));
        if (likesSidebarOpen) dispatch(setLikesSidebarOpen(false));
        if (likedMixesSidebarOpen) dispatch(setLikedMixesSidebarOpen(false));
        if (followedArtistsSidebarOpen) dispatch(setFollowedArtistsSidebarOpen(false));
        if (seratoSidebarOpen) dispatch(setSeratoSidebarOpen(false));
        if (traktorSidebarOpen) dispatch(setTraktorSidebarOpen(false));
        if (virtualdjSidebarOpen) dispatch(setVirtualDJSidebarOpen(false));
        dispatch(toggleRekordboxSidebar());
    };

    const handleOpenSeratoSidebar = () => {
        if (sidebarOpen) dispatch(setSidebarOpen(false));
        if (likesSidebarOpen) dispatch(setLikesSidebarOpen(false));
        if (likedMixesSidebarOpen) dispatch(setLikedMixesSidebarOpen(false));
        if (followedArtistsSidebarOpen) dispatch(setFollowedArtistsSidebarOpen(false));
        if (rekordboxSidebarOpen) dispatch(setRekordboxSidebarOpen(false));
        if (traktorSidebarOpen) dispatch(setTraktorSidebarOpen(false));
        if (virtualdjSidebarOpen) dispatch(setVirtualDJSidebarOpen(false));
        dispatch(toggleSeratoSidebar());
    };

    const handleOpenTraktorSidebar = () => {
        if (sidebarOpen) dispatch(setSidebarOpen(false));
        if (likesSidebarOpen) dispatch(setLikesSidebarOpen(false));
        if (likedMixesSidebarOpen) dispatch(setLikedMixesSidebarOpen(false));
        if (followedArtistsSidebarOpen) dispatch(setFollowedArtistsSidebarOpen(false));
        if (rekordboxSidebarOpen) dispatch(setRekordboxSidebarOpen(false));
        if (seratoSidebarOpen) dispatch(setSeratoSidebarOpen(false));
        if (virtualdjSidebarOpen) dispatch(setVirtualDJSidebarOpen(false));
        dispatch(toggleTraktorSidebar());
    };

    const handleOpenVirtualDJSidebar = () => {
        if (sidebarOpen) dispatch(setSidebarOpen(false));
        if (likesSidebarOpen) dispatch(setLikesSidebarOpen(false));
        if (likedMixesSidebarOpen) dispatch(setLikedMixesSidebarOpen(false));
        if (followedArtistsSidebarOpen) dispatch(setFollowedArtistsSidebarOpen(false));
        if (rekordboxSidebarOpen) dispatch(setRekordboxSidebarOpen(false));
        if (seratoSidebarOpen) dispatch(setSeratoSidebarOpen(false));
        if (traktorSidebarOpen) dispatch(setTraktorSidebarOpen(false));
        dispatch(toggleVirtualDJSidebar());
    };

    const handleOpenFollowedArtistsSidebar = () => {
        if (sidebarOpen) dispatch(setSidebarOpen(false));
        if (likesSidebarOpen) dispatch(setLikesSidebarOpen(false));
        if (likedMixesSidebarOpen) dispatch(setLikedMixesSidebarOpen(false));
        if (rekordboxSidebarOpen) dispatch(setRekordboxSidebarOpen(false));
        if (seratoSidebarOpen) dispatch(setSeratoSidebarOpen(false));
        if (traktorSidebarOpen) dispatch(setTraktorSidebarOpen(false));
        if (virtualdjSidebarOpen) dispatch(setVirtualDJSidebarOpen(false));
        dispatch(toggleFollowedArtistsSidebar());
    };

    const expandedOpen = asPage || sidebarOpen;
    const handleClose = () => {
        if (asPage) {
            // The in-page arrow is "up to Library", not browser Back: this
            // page is a Library sub-page, and history.back() sent the user
            // to whatever tab they visited before Library (e.g. Digger).
            // The OS/browser back gesture still walks history as before.
            dispatch(setActiveTab('library'));
        } else {
            dispatch(toggleSidebar());
        }
    };

    return (
        <>
            {/* DESIGN V2 (desktop): the sidebar is the navigation; no icon rail. */}
            {!asPage && designV2 && !isWeb && (
                <SideNav
                    panels={{ playlists: sidebarOpen, likes: likesSidebarOpen, artists: followedArtistsSidebarOpen, rekordbox: rekordboxSidebarOpen, serato: seratoSidebarOpen, traktor: traktorSidebarOpen, virtualdj: virtualdjSidebarOpen }}
                    onOpenPanel={(key) => ({
                        playlists: handleOpenPlaylistSidebar, likes: handleOpenLikesSidebar, artists: handleOpenFollowedArtistsSidebar,
                        rekordbox: handleOpenRekordboxSidebar, serato: handleOpenSeratoSidebar, traktor: handleOpenTraktorSidebar, virtualdj: handleOpenVirtualDJSidebar,
                    })[key]?.()}
                    onClosePanels={() => {
                        if (sidebarOpen) dispatch(toggleSidebar());
                        if (likesSidebarOpen) dispatch(setLikesSidebarOpen(false));
                        if (followedArtistsSidebarOpen) dispatch(setFollowedArtistsSidebarOpen(false));
                        if (rekordboxSidebarOpen) dispatch(setRekordboxSidebarOpen(false));
                        if (seratoSidebarOpen) dispatch(setSeratoSidebarOpen(false));
                        if (traktorSidebarOpen) dispatch(setTraktorSidebarOpen(false));
                        if (virtualdjSidebarOpen) dispatch(setVirtualDJSidebarOpen(false));
                    }}
                    djChangesPending={djChangesPending}
                    showDjApps={!!window.electron?.db}
                />
            )}
            {/* Collapsed icon sidebar */}
            {!asPage && !(designV2 && !isWeb) && (
            <div
                className={`playlist-sidebar-collapsed${railJustClicked ? ' just-clicked' : ''}`}
                onMouseLeave={handleRailMouseLeave}
            >
                <div className="top-buttons">
                    <button
                        className={`sidebar-toggle-btn ${sidebarOpen ? 'active' : ''}`}
                        onClick={() => { handleRailClick(); handleOpenPlaylistSidebar(); }}
                        title={sidebarOpen ? "Hide Playlists" : "Show Playlists"}
                    >
                        <div className="playlist-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 6H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M15 18H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="19" cy="18" r="2" stroke="currentColor" strokeWidth="2"/>
                                <path d="M21 6V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <span className="sidebar-label">Playlists</span>
                    </button>
                    <button
                        className={`sidebar-toggle-btn likes-toggle-btn ${likesSidebarOpen ? 'active' : ''}`}
                        onClick={() => { handleRailClick(); handleOpenLikesSidebar(); }}
                        title={likesSidebarOpen ? "Hide Liked Tracks" : "Liked Tracks"}
                    >
                        <div className="likes-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill={likesSidebarOpen ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                        </div>
                        <span className="sidebar-label">Liked Tracks</span>
                    </button>
                    <button
                        className={`sidebar-toggle-btn followed-artists-toggle-btn ${followedArtistsSidebarOpen ? 'active' : ''}`}
                        onClick={() => { handleRailClick(); handleOpenFollowedArtistsSidebar(); }}
                        title={followedArtistsSidebarOpen ? "Hide Favourite Artists" : "Favourite Artists"}
                    >
                        <div className="followed-artists-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <polygon points="18 12 19.8 15.5 23.5 16 21 18.5 21.5 22 18 20 14.5 22 15 18.5 12.5 16 16.2 15.5" fill={followedArtistsSidebarOpen ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                        </div>
                        <span className="sidebar-label">Favourite Artists</span>
                    </button>
                </div>

                {window.electron?.db && (
                <div className="dj-software-buttons">
                    <button
                        className={`sidebar-toggle-btn rekordbox-toggle-btn ${rekordboxSidebarOpen ? 'active' : ''}`}
                        onClick={() => { handleRailClick(); handleOpenRekordboxSidebar(); }}
                        title={rekordboxSidebarOpen ? "Hide Rekordbox" : "Rekordbox Sync"}
                    >
                        <img src={rekordboxLogo} alt="Rekordbox" className="rekordbox-icon" />
                        <span className="sidebar-label">Rekordbox</span>
                        {djChangesPending.rekordbox && <span className="rail-change-dot" title={`Updates from Rekordbox — ${djChangesPending.rekordbox.summary}`} />}
                    </button>
                    <button
                        className={`sidebar-toggle-btn serato-toggle-btn ${seratoSidebarOpen ? 'active' : ''}`}
                        onClick={() => { handleRailClick(); handleOpenSeratoSidebar(); }}
                        title={seratoSidebarOpen ? "Hide Serato" : "Serato Sync"}
                    >
                        <img src={seratoLogo} alt="Serato" className="serato-icon" />
                        <span className="sidebar-label">Serato</span>
                        {djChangesPending.serato && <span className="rail-change-dot" title={`Updates from Serato — ${djChangesPending.serato.summary}`} />}
                    </button>
                    <button
                        className={`sidebar-toggle-btn traktor-toggle-btn ${traktorSidebarOpen ? 'active' : ''}`}
                        onClick={() => { handleRailClick(); handleOpenTraktorSidebar(); }}
                        title={traktorSidebarOpen ? "Hide Traktor" : "Traktor Sync"}
                    >
                        <img src={traktorLogo} alt="Traktor" className="traktor-icon" />
                        <span className="sidebar-label">Traktor</span>
                        {djChangesPending.traktor && <span className="rail-change-dot" title={`Updates from Traktor — ${djChangesPending.traktor.summary}`} />}
                    </button>
                    <button
                        className={`sidebar-toggle-btn virtualdj-toggle-btn ${virtualdjSidebarOpen ? 'active' : ''}`}
                        onClick={() => { handleRailClick(); handleOpenVirtualDJSidebar(); }}
                        title={virtualdjSidebarOpen ? "Hide Virtual DJ" : "Virtual DJ Sync"}
                    >
                        <img src={virtualdjLogo} alt="Virtual DJ" className="virtualdj-icon" />
                        <span className="sidebar-label">Virtual DJ</span>
                        {djChangesPending.virtualdj && <span className="rail-change-dot" title={`Updates from Virtual DJ — ${djChangesPending.virtualdj.summary}`} />}
                    </button>
                </div>
                )}
            </div>
            )}

            {expandedOpen && (
            <div
                ref={expandedRef}
                className={`playlist-sidebar${asPage ? ' as-page' : ''}${isResizing ? ' resizing' : ''}`}
                style={asPage ? undefined : { width: `${sidebarWidth}px` }}
            >
                <div className={`sidebar-header${expandedPlaylistId ? ' is-detail' : ''}`}>
                    <div className="header-title">
                        {expandedPlaylistId ? (
                            <button
                                className="header-back-btn"
                                onClick={() => {
                                    const top = navIndex >= 0 ? navStack[navIndex] : null;
                                    if (designV2 && !asPage && top?.panel === 'playlists' && top.playlist === expandedPlaylistId && navIndex > 0) { dispatch(navigateHistory('back')); return; }
                                    setExpandedPlaylistId(null); setExpandedPlaylistTracks([]);
                                }}
                                title="Back to playlists"
                            >
                                <svg className="back-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                                <span>Playlists</span>
                            </button>
                        ) : (
                            <h4>Playlists</h4>
                        )}
                        <button
                            className="sidebar-collapse-btn"
                            onClick={handleClose}
                            title={asPage ? 'Back' : 'Hide Playlists'}
                        >
                            {asPage ? '←' : '‹'}
                        </button>
                    </div>
                    {!expandedPlaylistId && (
                        <div className="header-actions">
                            {designV2 && !asPage && <ViewToggle mode={viewMode} onChange={setViewMode} />}
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setShowCreateModal(true)}
                                className="create-playlist-btn"
                            >
                                {designV2 ? '+ New playlist' : '+ New Playlist'}
                            </Button>
                            {audioList.length > 0 && (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => dispatch(setShowSavePlayerModal(true))}
                                    className="save-player-btn"
                                    title={`Save current player queue (${audioList.length} tracks)`}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px' }}>
                                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <polyline points="7 3 7 8 15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    {designV2 ? 'Save queue' : 'Save Queue'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {!expandedPlaylistId && (
                    <div className="sidebar-search">
                        {isMobileWeb ? (
                            <MobileListSearch
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search playlists"
                                ariaLabel="Search playlists"
                                overlay={{
                                    fetch: (q) => playlists
                                        .filter(p => matchesQuery(p.name, q))
                                        .map((p) => {
                                            const n = p.trackCount ?? p.track_count ?? p.tracks?.length;
                                            return {
                                                key: `pl-${p.id}`,
                                                title: p.name,
                                                subtitle: n != null ? `${n} track${n === 1 ? '' : 's'}` : null,
                                                artworkUrl: playlistCoverUrl(p),
                                                item: p,
                                            };
                                        }),
                                    onSelect: (row) => handleLoadPlaylist(row.item.id),
                                }}
                            />
                        ) : (
                            <SidebarSearchField
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search playlists"
                                ariaLabel="Search playlists"
                            />
                        )}
                    </div>
                )}

                {!expandedPlaylistId && visibleSourceChips.length >= 2 && (
                    <div className="source-filter-chips" role="group" aria-label="Filter playlists by source">
                        {visibleSourceChips.map(s => (s === 'mixgoat'
                            ? { key: 'mixgoat', label: 'MixGoat', count: sourceCounts.mixgoat }
                            : { key: s, label: SOURCE_LABELS[s], count: sourceCounts[s], glyph: s }
                        )).map(chip => {
                            const isOn = activeSource === chip.key;
                            return (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className={`source-chip${isOn ? ' active' : ''}`}
                                    title={isOn
                                        ? `Clear ${chip.label} filter`
                                        : `${chip.label} — ${chip.count} playlist${chip.count === 1 ? '' : 's'}`}
                                    onClick={() => {
                                        const next = isOn ? 'all' : chip.key;
                                        setSourceFilter(next);
                                        if (next !== 'all') trackEvent('playlist_source_filter_applied', { source: next, count: chip.count });
                                    }}
                                >
                                    {chip.glyph
                                        ? <span className="chip-glyph"><SourceGlyph source={chip.glyph} size={13} /></span>
                                        : <span className="chip-glyph"><img src={mixgoatGlyph} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} /></span>}
                                    <span className="chip-count">{chip.count}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {selectedTracks.length > 0 && (
                    <div className="selection-info">
                        <span>{selectedTracks.length} track{selectedTracks.length !== 1 ? 's' : ''} selected</span>
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => dispatch(clearSelection())}
                        >
                            Clear
                        </Button>
                    </div>
                )}

                <div className="sidebar-content" ref={listScrollRef}>
                    {expandedPlaylistId ? (
                        <div className="playlist-track-view">
                            {(() => {
                                const expandedPlaylist = playlists.find(p => p.id === expandedPlaylistId);
                                const playlistName = expandedPlaylist?.name || 'Playlist';
                                const cover = usableCoverUrl(expandedPlaylist?.cover_image || expandedPlaylist?.coverImage);
                                const heroConnections = connectionsOf(expandedPlaylist, sourceLinks);
                                const firstTrackCover = usableCoverUrl(
                                    expandedPlaylistTracks.find(t => t.cover || t.artwork)?.cover
                                    || expandedPlaylistTracks.find(t => t.cover || t.artwork)?.artwork
                                ) || null;
                                const trackCount = expandedPlaylistTracks.length;
                                const missingCount = expandedPlaylistTracks.filter(t => t.missingLocal).length;
                                return (
                                    <div className="track-view-hero">
                                        <div className="track-view-hero-top">
                                            <PlaylistCoverImg
                                                src={cover || firstTrackCover}
                                                alt={playlistName}
                                                wrapClassName="hero-cover"
                                                fallbackClassName="hero-cover-placeholder"
                                                fallbackSize={26}
                                                seed={playlistName}
                                                sources={heroConnections}
                                            />
                                            <div className="hero-info">
                                                <div className="hero-title" title={playlistName}>{playlistName}</div>
                                                <div className="hero-meta">{trackCount} {trackCount === 1 ? 'track' : 'tracks'}</div>
                                                {heroConnections.length > 0 && (
                                                    <div
                                                        className="hero-connections"
                                                        // The merge guarantee, stated where a user
                                                        // wondering about multi-tool sync would look.
                                                        title="Changes from every connected tool are merged — MixGoat never removes tracks."
                                                    >
                                                        Syncs with {heroConnections.map(s => SOURCE_LABELS[s]).join(' & ')}
                                                    </div>
                                                )}
                                            </div>
                                        <button
                                            className={`hero-play-btn${playAllStarting ? ' loading' : ''}`}
                                            onClick={playAllStarting ? undefined : (ownsQueue ? handleTogglePlaylistPlayback : handlePlayAllPlaylist)}
                                            disabled={!trackCount}
                                            title={trackCount ? (playAllStarting ? 'Loading…' : ownsQueue ? (showPause ? 'Pause' : 'Resume') : 'Play all') : 'Empty playlist'}
                                        >
                                            {playAllStarting ? (
                                                <span className="hero-play-spinner" aria-hidden="true" />
                                            ) : showPause ? (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                    <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                                                </svg>
                                            ) : (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                    <polygon points="6 3 20 12 6 21 6 3" />
                                                </svg>
                                            )}
                                            {playAllStarting ? (designV2 ? 'Play all' : 'Play All') : ownsQueue ? (showPause ? 'Pause' : 'Resume') : (designV2 ? 'Play all' : 'Play All')}
                                        </button>
                                        </div>
                                        {/* Phrased as absence, not location: for a row with no
                                            source at all there is no "other device" — and this
                                            matches the row tooltip and the player's toasts. */}
                                        {missingCount > 0 && (
                                            <div className="hero-missing-note">
                                                {missingCount === trackCount
                                                    ? (trackCount === 1 ? "This track isn't on this device" : "These tracks aren't on this device")
                                                    : `${missingCount} of ${trackCount} tracks ${missingCount === 1 ? "isn't" : "aren't"} on this device`}
                                                {canRestoreFromBackup && restorableBackupItems(expandedPlaylistTracks).length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="hero-restore-link"
                                                        disabled={restoringMissing}
                                                        onClick={() => restoreMissingFromBackup(restorableBackupItems(expandedPlaylistTracks), 'missing_badge')}
                                                    >
                                                        {restoringMissing ? 'Restoring…' : 'Restore from Cloud Backup'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            {showPlaylistFilter && (
                                isMobileWeb ? (
                                    <div className="track-view-search-mobile">
                                        <MobileListSearch
                                            value={playlistFilter}
                                            onChange={handlePlaylistFilterChange}
                                            placeholder="Search in playlist"
                                            ariaLabel="Search tracks in this playlist"
                                            overlay={{
                                                fetch: (q) => expandedPlaylistTracks
                                                    .map((t, i) => ({ t, i }))
                                                    .filter(({ t }) => matchesQuery(rowSearchText(t), q))
                                                    .map(({ t, i }) => ({
                                                        key: `pt-${sortableIdOf(t, i)}`,
                                                        title: cleanDisplayTitle(t.name) || t.name || 'Unknown',
                                                        subtitle: t.singer || null,
                                                        artworkUrl: t.cover || null,
                                                        item: t,
                                                    })),
                                                onSelect: (row) => handleOpenInMixpedia(row.item),
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <SidebarSearchField
                                        value={playlistFilter}
                                        onChange={handlePlaylistFilterChange}
                                        placeholder="Search in playlist"
                                        ariaLabel="Search tracks in this playlist"
                                    />
                                )
                            )}
                            {expandedPlaylistLoading ? (
                                <div className="text-center p-4">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : expandedPlaylistTracks.length === 0 ? (
                                <div className="empty-state"><p>No tracks in this playlist</p></div>
                            ) : visibleTrackRows.length === 0 ? (
                                // Says what was searched AND how to get the rows back:
                                // an empty list with a filter still applied is the
                                // easiest place in the app to think tracks vanished.
                                <div className="empty-state track-view-no-match">
                                    <p>No tracks match &ldquo;{playlistFilter.trim()}&rdquo;</p>
                                    <Button variant="outline-primary" size="sm" onClick={() => setPlaylistFilter('')}>
                                        Show all {expandedPlaylistTracks.length} tracks
                                    </Button>
                                </div>
                            ) : (
                                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleTrackDragEnd}>
                                <SortableContext items={visibleTrackRows.map(({ track, idx }) => sortableIdOf(track, idx))} strategy={verticalListSortingStrategy}>
                                <div className={`track-view-list${canReorder ? ' reorderable' : ''}`}>
                                    {visibleTrackRows.map(({ track, idx }) => {
                                        const trackPath = track.musicSrc;
                                        // A mix row off-desktop is not "your file is on the other
                                        // machine" — it's "mixes play on desktop". Same non-playable
                                        // outcome, different reason, so it gets its own state instead
                                        // of borrowing the synced-away-file dimming.
                                        const isMobileMixRow = isWeb && !!(track.mixPublicId || track.mix_public_id);
                                        const isDesktopOnly = isWeb && !trackPath && !isMobileMixRow;
                                        const isCurrentTrack = trackPath && currentlyPlayingPath === trackPath;
                                        // Mix rows have no musicSrc to match against the
                                        // player — their play state comes from the mix
                                        // player's tracking key (the mix public id), the
                                        // same way feed/Mixpedia mix rows do.
                                        const mixPid = track.mixPublicId || track.mix_public_id || null;
                                        const isMixLoading = !!mixPid && loadingMixId === mixPid;
                                        const isActiveTrack = (isCurrentTrack && isPlaying) || (!!mixPid && playingMixId === mixPid);
                                        const adaptedTrack = adaptTrackForMixpedia(track);
                                        const rowClickProps = adaptedTrack.title
                                            ? delayedClick(() => handleOpenInMixpedia(track))
                                            : {};
                                        const sortableId = sortableIdOf(track, idx);
                                        return (
                                        <SortableTrackRow key={sortableId} id={sortableId} disabled={!canReorder}>
                                        {({ setNodeRef, style, dragProps, isDragging }) => (
                                        <div
                                            ref={setNodeRef}
                                            style={style}
                                            className={`track-view-item${isDesktopOnly ? ' desktop-only' : ''}${track.missingLocal ? ' not-on-device' : ''}${isActiveTrack ? ' playing' : ''}${adaptedTrack.title ? ' clickable' : ''}${isDragging ? ' dragging' : ''}`}
                                            title={adaptedTrack.title ? 'Open in Mixpedia' : undefined}
                                            {...rowClickProps}
                                            {...dragProps}
                                        >
                                            {isDesktopOnly ? (
                                                <div className="track-thumb">
                                                    {track.cover ? <CoverArt src={track.cover} size={48} className="track-thumb-img" /> : <div className="track-thumb-placeholder" />}
                                                    <div className="track-thumb-icon">
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                                                    </div>
                                                </div>
                                            ) : isMobileMixRow ? (
                                                <div
                                                    className="track-thumb"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenInMixpedia(track);
                                                    }}
                                                    style={{cursor:'pointer'}}
                                                    title="Open mix in Mixpedia — mixes play on the desktop app"
                                                >
                                                    {track.cover ? <CoverArt src={track.cover} size={48} className="track-thumb-img" /> : <div className="track-thumb-placeholder" />}
                                                    <div className="track-thumb-icon">
                                                        {/* Waveform bars — reads as "mix", not as a play/File affordance. */}
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                                                            <line x1="4" y1="9" x2="4" y2="15"/>
                                                            <line x1="9" y1="5" x2="9" y2="19"/>
                                                            <line x1="14" y1="8" x2="14" y2="16"/>
                                                            <line x1="19" y1="11" x2="19" y2="13"/>
                                                        </svg>
                                                    </div>
                                                </div>
                                            ) : isMixLoading ? (
                                                <div className="track-thumb" style={{cursor:'default'}} title="Loading mix…">
                                                    {track.cover ? <CoverArt src={track.cover} size={48} className="track-thumb-img" /> : <div className="track-thumb-placeholder" />}
                                                    <div className="track-thumb-icon">
                                                        <Spinner animation="border" variant="light" style={{ width: 10, height: 10, borderWidth: 1 }} />
                                                    </div>
                                                </div>
                                            ) : isActiveTrack ? (
                                                <div className="track-thumb" onClick={handlePauseTrack} style={{cursor:'pointer'}} title="Pause">
                                                    {track.cover ? <CoverArt src={track.cover} size={48} className="track-thumb-img" /> : <div className="track-thumb-placeholder" />}
                                                    <div className="track-thumb-icon">
                                                        <svg viewBox="0 0 24 24" width="10" height="10" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    className="track-thumb"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePlayTrackFromPlaylist(track, idx);
                                                    }}
                                                    style={{cursor:'pointer'}}
                                                    title={track.missingLocal ? "Not on this device — it's on the device you synced it from" : 'Play'}
                                                >
                                                    {track.cover ? <CoverArt src={track.cover} size={48} className="track-thumb-img" /> : <div className="track-thumb-placeholder" />}
                                                    <div className="track-thumb-icon">
                                                        <svg viewBox="0 0 24 24" width="10" height="10" fill="white"><polygon points="6 3 20 12 6 21 6 3"/></svg>
                                                    </div>
                                                    {track.missingLocal && (
                                                        <div className="track-thumb-offdevice">
                                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="m2 2 20 20"/>
                                                                <path d="M5.782 5.782A7 7 0 0 0 9 19h8.5a4.5 4.5 0 0 0 1.307-.193"/>
                                                                <path d="M21.532 16.5A4.5 4.5 0 0 0 17.5 10h-1.79A7.008 7.008 0 0 0 10 5.07"/>
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <div className="track-info">
                                                <span className="track-title">{cleanDisplayTitle(track.name) || track.musicSrc?.split('/').pop() || 'Unknown'}</span>
                                                {(track.singer || isDesktopOnly) && (
                                                    <span className="track-subline">
                                                        {track.singer && <span className="track-artist">{track.singer}</span>}
                                                        {isDesktopOnly && <span className="track-desktop-badge">Desktop only</span>}
                                                    </span>
                                                )}
                                            </div>
                                            {!isDesktopOnly && adaptedTrack.title && (
                                                <TrackContextMenu track={adaptedTrack} onSearch={handleContextMenuSearch} onRemoveFromLibrary={useServer && adaptedTrack.id ? handleRemoveFromPlaylist : undefined} removeLabel="Remove from Playlist" desktopLike analyticsFrom="playlist_sidebar" onRestoreFromBackup={canRestoreFromBackup && restorableBackupItems([track]).length > 0 ? () => restoreMissingFromBackup(restorableBackupItems([track]), 'missing_badge') : undefined} />
                                            )}
                                        </div>
                                        )}
                                        </SortableTrackRow>
                                        );
                                    })}
                                </div>
                                </SortableContext>
                                </DndContext>
                            )}
                        </div>
                    ) : loading ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" size="sm" />
                        </div>
                    ) : filteredPlaylists.length === 0 ? (
                        <div className="empty-state">
                            {searchQuery ? (
                                <p>No playlists found</p>
                            ) : (
                                <>
                                    <p>No playlists yet</p>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        Create your first playlist
                                    </Button>
                                </>
                            )}
                        </div>
                    ) : (
                        <ListGroup variant="flush" className={`playlist-list${designV2 && !asPage && viewMode === 'list' ? ' is-list' : ''}`}>
                            {[
                                ...filteredPlaylists.filter(nameMatchesQuery),
                                ...(filteredPlaylists.some(p => !nameMatchesQuery(p))
                                    ? [{ __trackGroupHeader: true, id: '__track-group-header' }]
                                    : []),
                                // Most matches first — with a common query the
                                // playlist that's actually about it floats up.
                                ...filteredPlaylists.filter(p => !nameMatchesQuery(p))
                                    .sort((a, b) => (trackMatchFor(b)?.matchCount || 0) - (trackMatchFor(a)?.matchCount || 0)),
                            ].map((playlist) => (
                                playlist.__trackGroupHeader ? (
                                    <div key="__track-group-header" className="playlist-group-header">
                                        Containing &ldquo;{searchQuery.trim()}&rdquo;
                                    </div>
                                ) :
                                <SwipeToRevealRow
                                    key={playlist.id}
                                    enabled={isTouchPhone}
                                    actions={
                                        <button type="button" className="swipe-row-action" onClick={() => handleDeletePlaylist(playlist.id, playlist.name)}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
                                            Delete
                                        </button>
                                    }
                                >
                                <ListGroup.Item
                                    className={`playlist-item ${selectedPlaylistId === playlist.id ? 'active' : ''}${trackMatchFor(playlist) && !nameMatchesQuery(playlist) ? ' track-match-row' : ''}`}
                                    onClick={(e) => {
                                        const m = trackMatchFor(playlist);
                                        if (m && !nameMatchesQuery(playlist)) trackEvent('playlist_opened_from_track_search', { match_count: m.matchCount });
                                        handleLoadPlaylist(playlist.id);
                                    }}
                                    onContextMenu={(e) => handleContextMenu(e, playlist)}
                                    // Only a real user-written description earns a row
                                    // tooltip. Imported playlists carry a machine
                                    // "Imported from <App>" description — that info
                                    // lives on the cover badge, not the whole row.
                                    title={!importSourceOf(playlist) && playlist.description ? playlist.description : undefined}
                                >
                                    {(() => {
                                        const cover = usableCoverUrl(playlist.cover_image || playlist.coverImage);
                                        const firstTrackCover = usableCoverUrl(
                                            (!cover && playlist.tracks?.find(t => t.track?.cover || t.cover)?.track?.cover)
                                            || (!cover && playlist.tracks?.find(t => t.track?.cover || t.cover)?.cover)
                                        );
                                        return (
                                            <PlaylistCoverImg
                                                src={cover || firstTrackCover}
                                                alt={playlist.name}
                                                wrapClassName="playlist-cover-thumb"
                                                fallbackClassName="playlist-cover-placeholder"
                                                fallbackSize={20}
                                                seed={playlist.name}
                                                sources={connectionsOf(playlist, sourceLinks)}
                                            />
                                        );
                                    })()}
                                    <div className="playlist-info">
                                        <div
                                            className="playlist-name"
                                            // Full name on hover, but ONLY when the
                                            // ellipsis actually kicked in — a tooltip
                                            // repeating visible text is noise.
                                            onMouseEnter={(e) => {
                                                const el = e.currentTarget;
                                                el.title = el.scrollWidth > el.clientWidth ? playlist.name : '';
                                            }}
                                        >{playlist.name}</div>
                                        <div className="playlist-meta">
                                            <Badge bg="secondary" className="track-count">
                                                {playlist.trackCount} tracks
                                            </Badge>
                                            {(() => {
                                                // Why this row is in the "Containing ..." group.
                                                // On these rows the match IS the meta line —
                                                // .track-match-row hides badge + Open-in so the
                                                // matched track gets the full width and stays
                                                // readable instead of a 60px ellipsis stub.
                                                const m = trackMatchFor(playlist);
                                                return (m && !nameMatchesQuery(playlist)) ? (
                                                    <span className="playlist-track-match" title={m.firstMatch}>
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                                                        <span className="match-label">{m.firstMatch}</span>
                                                        {m.matchCount > 1 && <span className="match-count">+{m.matchCount - 1} more</span>}
                                                    </span>
                                                ) : null;
                                            })()}
                                            <div className="dropdown-container" ref={openDropdownId === playlist.id ? dropdownRef : null} onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className="open-in-btn"
                                                    onClick={() => setOpenDropdownId(openDropdownId === playlist.id ? null : playlist.id)}
                                                >
                                                    Open in ▾
                                                </button>
                                                {openDropdownId === playlist.id && (
                                                    <div className={`action-dropdown${openDropdownUp ? ' open-up' : ''}`} ref={actionDropdownRef}>
                                                        <div className="dropdown-header">
                                                            <span className="header-label">Click to load, + to add</span>
                                                        </div>
                                                        <div className="dropdown-item" onClick={() => handleOpenInTab(playlist.id, 'visualize', false)}>
                                                            <span className="item-label"><VisualizeIcon className="dropdown-icon" /> Library</span>
                                                            <span className="item-actions">
                                                                <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(playlist.id, 'visualize', true); }} title="Add to existing">+</button>
                                                            </span>
                                                        </div>
                                                        <div className="dropdown-item" onClick={() => handleOpenInTab(playlist.id, 'fix-my-tracks', false)}>
                                                            <span className="item-label"><AnalyzeIcon className="dropdown-icon" /> Compatibility</span>
                                                            <span className="item-actions">
                                                                <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(playlist.id, 'fix-my-tracks', true); }} title="Add to existing">+</button>
                                                            </span>
                                                        </div>
                                                        <div className="dropdown-item" onClick={() => handleOpenInTab(playlist.id, 'match-loudness', false)}>
                                                            <span className="item-label"><LoudnessIcon className="dropdown-icon" /> Loudness</span>
                                                            <span className="item-actions">
                                                                <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(playlist.id, 'match-loudness', true); }} title="Add to existing">+</button>
                                                            </span>
                                                        </div>
                                                        <div className="dropdown-item" onClick={() => handleOpenInTab(playlist.id, 'organize-tracks', false)}>
                                                            <span className="item-label"><OrganizeIcon className="dropdown-icon" /> Organize</span>
                                                            <span className="item-actions">
                                                                <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(playlist.id, 'organize-tracks', true); }} title="Add to existing">+</button>
                                                            </span>
                                                        </div>
                                                        <div className="dropdown-item" onClick={() => handleOpenInTab(playlist.id, 'fix-metadata', false)}>
                                                            <span className="item-label"><MetadataIcon className="dropdown-icon" /> Metadata</span>
                                                            <span className="item-actions">
                                                                <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(playlist.id, 'fix-metadata', true); }} title="Add to existing">+</button>
                                                            </span>
                                                        </div>
                                                        <div className="dropdown-item" onClick={() => handleOpenInTab(playlist.id, 'smart-recs', false)}>
                                                            <span className="item-label">
                                                                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                </svg>
                                                                Crate Digger
                                                            </span>
                                                        </div>
                                                        <div className="dropdown-divider"></div>
                                                        <div className="dropdown-item" onClick={() => { setOpenDropdownId(null); handleLoadIntoPlayerQueue(playlist.id, false); }}>
                                                            <span className="item-label">
                                                                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                </svg>
                                                                Player Queue
                                                            </span>
                                                            <span className="item-actions">
                                                                <button onClick={(e) => { e.stopPropagation(); setOpenDropdownId(null); handleLoadIntoPlayerQueue(playlist.id, true); }} title="Add to existing">+</button>
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="playlist-actions" onClick={(e) => e.stopPropagation()}>
                                        {selectedTracks.length > 0 && (
                                            <button
                                                className="action-btn add-btn"
                                                onClick={() => handleAddTracksToPlaylist(playlist.id)}
                                                title="Add selected tracks"
                                            >
                                                +
                                            </button>
                                        )}
                                        <button
                                            className="action-btn edit-btn"
                                            onClick={() => openRenameModal(playlist)}
                                            title="Edit playlist"
                                        >
                                            ✎
                                        </button>
                                        <button
                                            className="action-btn delete-btn"
                                            onClick={() => handleDeletePlaylist(playlist.id, playlist.name)}
                                            title="Delete playlist"
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <PlaylistRowMenu
                                        playlist={playlist}
                                        onEdit={(p) => openRenameModal(p)}
                                        onDelete={(p) => handleDeletePlaylist(p.id, p.name)}
                                        onPlayerQueue={(id) => handleLoadIntoPlayerQueue(id, false)}
                                    />
                                </ListGroup.Item>
                                </SwipeToRevealRow>
                            ))}
                        </ListGroup>
                    )}
                </div>

                <div
                    className="sidebar-resizer"
                    onMouseDown={() => setIsResizing(true)}
                />
            </div>
            )}

            {/* Create Playlist Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{designV2 ? 'New playlist' : 'Create New Playlist'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>{designV2 ? 'Cover (optional)' : 'Cover Image (optional)'}</Form.Label>
                            <div className="cover-image-upload">
                                {coverImage ? (
                                    <div className="cover-preview">
                                        <img src={coverImage} alt="Cover preview" />
                                        <button
                                            type="button"
                                            className="remove-cover-btn"
                                            onClick={() => setCoverImage(null)}
                                            title="Remove image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <label
                                        className={`cover-upload-label ${isDraggingOver ? 'dragging-over' : ''}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ display: 'none' }}
                                        />
                                        <div className="upload-placeholder">
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span>{isDraggingOver ? 'Drop image here' : (designV2 ? 'Drop an image, or click to choose' : 'Click or drag to upload image')}</span>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{designV2 ? 'Name' : 'Playlist Name *'}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={designV2 ? 'Playlist name' : 'Enter playlist name'}
                                value={newPlaylistName}
                                onChange={(e) => setNewPlaylistName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreatePlaylist(); } }}
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description (optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder={designV2 ? 'Description' : 'Enter description'}
                                value={newPlaylistDescription}
                                onChange={(e) => setNewPlaylistDescription(e.target.value)}
                            />
                        </Form.Group>
                        {selectedTracks.length > 0 && (
                            <div className="text-muted small">
                                {selectedTracks.length} selected track{selectedTracks.length !== 1 ? 's' : ''} will be added
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreatePlaylist}>
                        {designV2 ? 'Create' : 'Create Playlist'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Playlist Modal */}
            <Modal show={showRenameModal} onHide={() => setShowRenameModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{designV2 ? 'Edit playlist' : 'Edit Playlist'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Cover Image (optional)</Form.Label>
                            <div className="cover-image-upload">
                                {coverImage ? (
                                    <div className="cover-preview">
                                        <img src={coverImage} alt="Cover preview" />
                                        <button
                                            type="button"
                                            className="remove-cover-btn"
                                            onClick={() => setCoverImage(null)}
                                            title="Remove image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <label
                                        className={`cover-upload-label ${isDraggingOver ? 'dragging-over' : ''}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ display: 'none' }}
                                        />
                                        <div className="upload-placeholder">
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span>{isDraggingOver ? 'Drop image here' : 'Click or drag to upload image'}</span>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Playlist Name *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter playlist name"
                                value={newPlaylistName}
                                onChange={(e) => setNewPlaylistName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleRenamePlaylist(); } }}
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description (optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder={designV2 ? 'Description' : 'Enter description'}
                                value={newPlaylistDescription}
                                onChange={(e) => setNewPlaylistDescription(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRenameModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleRenamePlaylist}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Save Player Queue Modal */}
            <Modal
                show={showSavePlayerModal}
                onHide={() => dispatch(setShowSavePlayerModal(false))}
                centered
                style={{ zIndex: 10000 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Save Player Queue as Playlist</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Playlist Name *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter playlist name"
                                value={newPlaylistName}
                                onChange={(e) => setNewPlaylistName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSavePlayerQueue(); } }}
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Cover Image (optional)</Form.Label>
                            <div className="cover-image-upload">
                                {coverImage ? (
                                    <div className="cover-preview">
                                        <img src={coverImage} alt="Cover preview" />
                                        <button
                                            type="button"
                                            className="remove-cover-btn"
                                            onClick={() => setCoverImage(null)}
                                            title="Remove image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <label
                                        className={`cover-upload-label ${isDraggingOver ? 'dragging-over' : ''}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ display: 'none' }}
                                        />
                                        <div className="upload-placeholder">
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span>{isDraggingOver ? 'Drop image here' : 'Click or drag to upload image'}</span>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description (optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder={designV2 ? 'Description' : 'Enter description'}
                                value={newPlaylistDescription}
                                onChange={(e) => setNewPlaylistDescription(e.target.value)}
                            />
                        </Form.Group>
                        <div className="text-muted small">
                            This will save {audioList.length} track{audioList.length !== 1 ? 's' : ''} from the player
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => dispatch(setShowSavePlayerModal(false))}>
                        Cancel
                    </Button>
                    <Button variant="success" onClick={handleSavePlayerQueue}>
                        Save Player Queue
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Playlist Context Menu */}
            {contextMenu && contextMenuPlaylist && (
                <div
                    ref={contextMenuRef}
                    className="playlist-context-menu"
                    style={{
                        position: 'fixed',
                        top: contextMenuPos ? contextMenuPos.top : contextMenu.y,
                        left: contextMenuPos ? contextMenuPos.left : contextMenu.x,
                        visibility: contextMenuPos ? 'visible' : 'hidden',
                        zIndex: 10002
                    }}
                >
                    <div className="context-menu-header">
                        <span>{contextMenuPlaylist.name}</span>
                    </div>
                    <div className="context-menu-divider" />
                    <div className="context-menu-item" onClick={() => handleOpenInTab(contextMenuPlaylist.id, 'visualize', false)}>
                        <span className="item-label"><VisualizeIcon className="dropdown-icon" /> Library</span>
                        <span className="item-actions">
                            <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(contextMenuPlaylist.id, 'visualize', true); }} title="Add to existing">+</button>
                        </span>
                    </div>
                    <div className="context-menu-item" onClick={() => handleOpenInTab(contextMenuPlaylist.id, 'fix-my-tracks', false)}>
                        <span className="item-label"><AnalyzeIcon className="dropdown-icon" /> Compatibility</span>
                        <span className="item-actions">
                            <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(contextMenuPlaylist.id, 'fix-my-tracks', true); }} title="Add to existing">+</button>
                        </span>
                    </div>
                    <div className="context-menu-item" onClick={() => handleOpenInTab(contextMenuPlaylist.id, 'match-loudness', false)}>
                        <span className="item-label"><LoudnessIcon className="dropdown-icon" /> Loudness</span>
                        <span className="item-actions">
                            <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(contextMenuPlaylist.id, 'match-loudness', true); }} title="Add to existing">+</button>
                        </span>
                    </div>
                    <div className="context-menu-item" onClick={() => handleOpenInTab(contextMenuPlaylist.id, 'organize-tracks', false)}>
                        <span className="item-label"><OrganizeIcon className="dropdown-icon" /> Organize</span>
                        <span className="item-actions">
                            <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(contextMenuPlaylist.id, 'organize-tracks', true); }} title="Add to existing">+</button>
                        </span>
                    </div>
                    <div className="context-menu-item" onClick={() => handleOpenInTab(contextMenuPlaylist.id, 'fix-metadata', false)}>
                        <span className="item-label"><MetadataIcon className="dropdown-icon" /> Metadata</span>
                        <span className="item-actions">
                            <button onClick={(e) => { e.stopPropagation(); handleOpenInTab(contextMenuPlaylist.id, 'fix-metadata', true); }} title="Add to existing">+</button>
                        </span>
                    </div>
                    <div className="context-menu-item" onClick={() => handleOpenInTab(contextMenuPlaylist.id, 'smart-recs', false)}>
                        <span className="item-label">
                            <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Crate Digger
                        </span>
                    </div>
                    <div className="context-menu-divider" />
                    <div className="context-menu-item" onClick={() => { setContextMenu(null); handleLoadPlaylist(contextMenuPlaylist.id, false); }}>
                        <span className="item-label">
                            <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Player Queue
                        </span>
                        <span className="item-actions">
                            <button onClick={(e) => { e.stopPropagation(); setContextMenu(null); handleLoadPlaylist(contextMenuPlaylist.id, true); }} title="Add to existing">+</button>
                        </span>
                    </div>
                </div>
            )}
        </>
    );
};

export default PlaylistSidebar;
